import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './CheckoutTheme.css';
import { getWebsiteThemeAttribution } from '../../experiments/websiteTheme';
import {
  Alert,
  Container,
  Row,
  Col,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Toast,
  ToastContainer,
} from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import CheckoutStripeElements from '../../components/CheckoutStripeElements';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { FaTrashAlt, FaCreditCard, FaLock, FaTag, FaTimes, FaCheck } from 'react-icons/fa';
import TrustStrip from '../../components/TrustStrip';
import StripeErrorBoundary from '../../components/StripeErrorBoundary';
import {
  CheckoutContactFields,
  CheckoutPaymentContainer,
  CheckoutCTAButton,
  HowFoundUsDropdown,
} from '../../components/CheckoutFormShared';
import { SmsConsentCheckbox, buildSmsConsentPayload } from '../../components/SmsConsent';
import { ConfirmationModal } from '../../components/UnifiedModal';
import { useCheckoutCart } from '../../contexts/CheckoutCartContext';
import useProductCatalog from '../../hooks/useProductCatalog';
import { validatePromoCode } from '../../api/checkout';
import useSavedPaymentMethods from '../../hooks/useSavedPaymentMethods';
import useUser from '../../hooks/useUser';
import useCheckoutExperienceExperiment from '../../hooks/useCheckoutExperienceExperiment';
import HostedCheckoutLauncher from '../../components/HostedCheckout/HostedCheckoutLauncher';
import { usePaymentStatus } from '../../contexts/PaymentStatusContext';
import {
  pushEvent,
  ANALYTICS_EVENTS,
  generateCheckoutTransactionId,
  identifyUserFromLeadCapture,
  trackBeginCheckout,
} from '../../utils/analytics';
import { checkoutAnalyticsValue } from '../../utils/checkoutAnalyticsValue';
import {
  getAffiliateCodeForApi,
  getUtmParamsForApi,
  getAttributionParams,
} from '../../utils/utmTracker';
import { loadContactFromSession, saveContactToSession } from '../../utils/aiPromptStorage';
import { convertLeadToTrial } from '../../utils/stripeConversion';
import { confirmSetupWithClient, confirmPaymentWithClient } from '../../utils/stripeClient';
import fetchWithTimeout from '../../utils/fetchWithTimeout';
import { createCheckoutAttemptId } from '../../utils/checkoutAttemptId';
import { classifyCheckoutResponse } from '../../utils/checkoutResponse';
import { completeStripeAutoLogin } from '../../utils/stripeAutoLogin';
import { applyThemeMode, useThemeMode } from '../../design-system';
import { ACTIVE_EXPERIMENTS } from '../../utils/experiments';

import {
  buildPostPurchaseChallengePath,
  isStarterOrLightProduct,
} from '../../utils/postSignupJourney';
import { PUBLIC_PRICING_TIERS as CHECKOUT_PRICING_TIERS } from '../../site/publicPricingSource';

import './CheckoutPage.css';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PK || '';
const API_URL = process.env.REACT_APP_API_URL;
const TERMS_DOC_TYPE = 'main_tos';
const PAYMENT_INTENT_FETCH_TIMEOUT_MS = 45000;
const AUTO_LOGIN_FETCH_TIMEOUT_MS = 10000;
const RESTRICT_REMOVE_FLOWS = new Set(['sales_ai_prompt']);

const CHECKOUT_THEME_VARIANTS = ['light', 'dark'];
const SHOULD_LOG_CHECKOUT_DEBUG = process.env.NODE_ENV !== 'production';

export const getCheckoutProductDisplayName = (product) => {
  const productSlug = product?.slug || product?.productSlug;
  const publicTier = productSlug
    ? CHECKOUT_PRICING_TIERS.find((candidate) => candidate?.productSlug === productSlug)
    : null;

  return publicTier?.name || product?.name || 'Unavailable item';
};

const normalizeFallbackQuantity = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return 1;
  }
  return Math.max(1, Math.min(10, Math.floor(parsed)));
};

const normalizeCheckoutFallbackItems = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      if (!item || typeof item.productSlug !== 'string' || !item.productSlug.trim()) {
        return null;
      }

      return {
        productSlug: item.productSlug.trim(),
        planKey:
          typeof item.planKey === 'string' && item.planKey.trim() ? item.planKey.trim() : 'default',
        quantity: normalizeFallbackQuantity(item.quantity),
      };
    })
    .filter(Boolean);
};

export const parseCheckoutFallbackFromQuery = (search = '') => {
  const params = new URLSearchParams(search || '');
  const productSlugs = params.getAll('product').filter(Boolean);
  const legacyProductSlug = params.get('productSlug');
  if (!productSlugs.length && legacyProductSlug) {
    productSlugs.push(legacyProductSlug);
  }
  if (!productSlugs.length) {
    return null;
  }

  const planKeys = params.getAll('planKey');
  const quantities = params.getAll('quantity');
  const items = normalizeCheckoutFallbackItems(
    productSlugs.map((productSlug, index) => ({
      productSlug,
      planKey: planKeys[index] || 'default',
      quantity: quantities[index] || 1,
    })),
  );

  if (!items.length) {
    return null;
  }

  return {
    items,
    flowContext: params.get('flowContext') || 'checkout_url',
    metadata: null,
    source: 'checkout_query',
  };
};

const getCheckoutFallbackCart = (location) => {
  const stateFallback = location?.state?.checkoutCartFallback;
  const stateItems = normalizeCheckoutFallbackItems(stateFallback?.items);
  if (stateItems.length) {
    return {
      items: stateItems,
      flowContext: stateFallback.flowContext || null,
      metadata: stateFallback.metadata || null,
      source: stateFallback.source || 'navigation_state',
      cartSynced: Boolean(stateFallback.cartSynced),
    };
  }

  return parseCheckoutFallbackFromQuery(location?.search || '');
};

export const buildCheckoutFallbackCatalogProduct = (item) => {
  const tier = CHECKOUT_PRICING_TIERS.find(
    (candidate) => candidate?.productSlug === item?.productSlug,
  );

  if (!tier || typeof tier.price !== 'number') {
    return null;
  }

  const isSubscription = tier.type === 'subscription';
  const planKey = item.planKey || tier.planKey || 'default';
  const supportedFallbackPlanKeys = new Set(['default', 'monthly', tier.planKey].filter(Boolean));
  if (isSubscription && !supportedFallbackPlanKeys.has(planKey)) {
    return null;
  }
  const amount = Math.round(tier.price * 100);

  return {
    slug: tier.productSlug,
    name: tier.name,
    description: tier.tagline || '',
    metadata: {
      isRecurring: isSubscription,
      source: 'public_pricing_fallback',
    },
    plans: [
      {
        planKey,
        label: isSubscription ? 'Monthly' : 'One-time',
        amount,
        currency: 'USD',
        default: true,
        ...(isSubscription
          ? {
              interval: 'month',
              intervalCount: 1,
              trialDays: 0,
            }
          : {}),
      },
    ],
  };
};

const mergeCatalogWithFallbackProducts = (catalog, fallbackCart) => {
  const baseProducts = Array.isArray(catalog?.products) ? catalog.products : [];
  const fallbackProducts = normalizeCheckoutFallbackItems(fallbackCart?.items)
    .map(buildCheckoutFallbackCatalogProduct)
    .filter(Boolean);

  if (!fallbackProducts.length) {
    return catalog;
  }

  const productsBySlug = new Map(baseProducts.map((product) => [product.slug, product]));
  fallbackProducts.forEach((product) => {
    if (!productsBySlug.has(product.slug)) {
      productsBySlug.set(product.slug, product);
    }
  });

  return {
    ...(catalog || {}),
    products: Array.from(productsBySlug.values()),
  };
};

// Sentinel to represent the "not yet resolved" saved-card selection state.
const INITIAL_SAVED_PAYMENT_METHOD = Symbol('INITIAL_SAVED_PAYMENT_METHOD');

const PAYMENT_MODES = Object.freeze({
  SAVED: 'saved',
  NEW: 'new',
});

const formatCurrency = (amount, currency = 'USD') => {
  if (typeof amount !== 'number') {
    return '';
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount / 100);
  } catch (error) {
    console.warn('[CheckoutPage] Failed to format currency', error, {
      amount,
      currency,
    });
    return `$${(amount / 100).toFixed(2)}`;
  }
};

const readInstallmentCount = (plan) => {
  const parsed = Number(plan?.metadata?.installments);
  return Number.isInteger(parsed) && parsed > 1 ? parsed : null;
};

const buildInstallmentDisclosure = (plan) => {
  const installments = readInstallmentCount(plan);
  const perInstallment = plan?.discountedAmount ?? plan?.amount;
  if (!installments || typeof perInstallment !== 'number') {
    return '';
  }

  const intervalLabel = plan?.interval === 'month' ? 'monthly' : 'scheduled';
  return `${installments} ${intervalLabel} payments of ${formatCurrency(perInstallment, plan.currency)}. Total commitment: ${formatCurrency(perInstallment * installments, plan.currency)}.`;
};

const buildGuaranteeDisclosure = (product) => {
  const guaranteeDays = Number(product?.metadata?.guaranteeDays);
  return guaranteeDays === 30 ? '30-day money-back guarantee' : '';
};

const buildTermsPayload = () => {
  const timestamp = new Date().toISOString();
  return {
    docType: TERMS_DOC_TYPE,
    version: timestamp.split('T')[0],
    acceptedAt: timestamp,
    hash: null,
  };
};

const CHECKOUT_PROFILE = {
  heroTitle: 'Checkout',
  heroSubtitle: 'Review your order and complete your purchase.',
  showTrustStrip: false,
  banner: (metadata) => metadata?.origin?.bannerMessage || null,
  ctaLabel: 'Complete Purchase',
  ctaProcessingLabel: 'Processing your payment…',
  ctaSparkle: false,
  termsAdditionalText: 'and authorize this purchase.',
  termsAcceptanceError: 'Please accept the Terms of Service to continue.',
  orderSummaryTitle: 'Order summary',
  reassuranceItems: [],
  paymentHighlights: [],
};

const deriveCartItems = (cart, catalog, metadata) => {
  const metadataProducts = Array.isArray(metadata?.requestedProducts)
    ? metadata.requestedProducts
    : [];
  const metadataBySlug = new Map(
    metadataProducts
      .filter((entry) => entry && typeof entry.productSlug === 'string')
      .map((entry) => [entry.productSlug, entry]),
  );

  if (!cart?.items || !Array.isArray(cart.items)) {
    return { items: [], missing: [] };
  }

  const missing = [];
  const deduped = new Map();
  cart.items.forEach((item) => {
    if (!item) return;
    const slug = typeof item.productSlug === 'string' ? item.productSlug : '';
    const planKey = typeof item.planKey === 'string' ? item.planKey : '';
    if (!slug || !planKey) return;
    const key = `${slug}::${planKey}`;
    deduped.set(key, {
      ...item,
      productSlug: slug,
      planKey,
      quantity: item.quantity ?? 1,
    });
  });
  const normalizedItems = Array.from(deduped.values());

  const items = normalizedItems.map((item) => {
    const product =
      catalog.products.find((candidate) => candidate.slug === item.productSlug) || null;
    const metadataEntry = metadataBySlug.get(item.productSlug) || null;

    if (!product) {
      missing.push({
        productSlug: item.productSlug,
        planKey: item.planKey,
        reason: 'missing_product',
      });
      return {
        raw: item,
        product: null,
        plan: null,
        allPlans: [],
      };
    }

    let plans = Array.isArray(product.plans) ? product.plans.map((plan) => ({ ...plan })) : [];

    if (metadataEntry && plans.length) {
      plans = plans.map((plan) => {
        if (plan.planKey !== item.planKey) {
          return { ...plan };
        }
        const updatedPlan = { ...plan };
        if (
          typeof updatedPlan.amount !== 'number' &&
          typeof metadataEntry.unitAmount === 'number'
        ) {
          updatedPlan.amount = metadataEntry.unitAmount;
        }
        if (
          !updatedPlan.currency &&
          typeof metadataEntry.currency === 'string' &&
          metadataEntry.currency.trim()
        ) {
          updatedPlan.currency = metadataEntry.currency.trim();
        }
        if (
          !updatedPlan.interval &&
          typeof metadataEntry.interval === 'string' &&
          metadataEntry.interval.trim()
        ) {
          updatedPlan.interval = metadataEntry.interval.trim();
        }
        if (
          updatedPlan.interval &&
          typeof updatedPlan.intervalCount !== 'number' &&
          typeof metadataEntry.intervalCount === 'number'
        ) {
          updatedPlan.intervalCount = metadataEntry.intervalCount;
        }
        if (
          !updatedPlan.label &&
          typeof metadataEntry.label === 'string' &&
          metadataEntry.label.trim()
        ) {
          updatedPlan.label = metadataEntry.label.trim();
        }
        return updatedPlan;
      });
    }

    const selectedPlan = plans.find((plan) => plan.planKey === item.planKey) || null;

    if (!selectedPlan) {
      missing.push({
        productSlug: item.productSlug,
        planKey: item.planKey,
        reason: 'missing_plan',
      });
    }

    if (selectedPlan && typeof selectedPlan.amount !== 'number') {
      missing.push({
        productSlug: item.productSlug,
        planKey: selectedPlan.planKey,
        reason: 'missing_price',
      });
    }

    if (selectedPlan?.checkoutAvailability?.purchasable === false) {
      missing.push({
        productSlug: item.productSlug,
        planKey: selectedPlan.planKey,
        reason: 'checkout_unavailable',
        message:
          selectedPlan.checkoutAvailability.message ||
          'Enrollment is not currently available for this plan.',
      });
    }

    const resolvedProduct = { ...product, plans };

    return {
      raw: item,
      product: resolvedProduct,
      plan: selectedPlan,
      allPlans: plans,
    };
  });

  return {
    items,
    missing,
  };
};

const computeTotals = (derivedItems) => {
  let recurringCents = 0;
  let oneTimeCents = 0;
  let trialDays = null;
  let currency = 'USD';
  const recurringTotalsByCadence = new Map();

  const recurringCadence = (plan) => {
    const interval = plan.interval;
    const intervalCount = Number(plan.intervalCount || 1);

    if (
      (interval === 'year' && intervalCount === 1) ||
      (interval === 'month' && intervalCount === 12)
    ) {
      return { key: 'year:1', label: 'year' };
    }

    if (intervalCount === 1) {
      return { key: `${interval}:1`, label: interval };
    }

    const pluralInterval = interval.endsWith('s') ? interval : `${interval}s`;
    return {
      key: `${interval}:${intervalCount}`,
      label: `${intervalCount} ${pluralInterval}`,
    };
  };

  derivedItems.forEach(({ plan, raw, product }) => {
    if (!plan || typeof plan.amount !== 'number') {
      return;
    }
    const quantity = raw.quantity ?? 1;
    // Use discountedAmount when available (from promotions), otherwise use original amount
    const effectiveAmount = plan.discountedAmount ?? plan.amount;
    const amount = effectiveAmount * quantity;
    currency = plan.currency || currency;
    if (plan.interval) {
      recurringCents += amount;
      const cadence = recurringCadence(plan);
      const cadenceKey = `${currency}:${cadence.key}`;
      const currentCadenceTotal = recurringTotalsByCadence.get(cadenceKey);
      recurringTotalsByCadence.set(cadenceKey, {
        amountCents: (currentCadenceTotal?.amountCents || 0) + amount,
        currency,
        cadenceLabel: cadence.label,
      });
      if (trialDays === null && typeof plan.trialDays === 'number') {
        trialDays = plan.trialDays;
      }
    } else if (product?.metadata?.isRecurring === false) {
      oneTimeCents += amount;
    } else {
      // Default to recurring if interval defined, otherwise treat as one-time
      if (plan.interval) {
        recurringCents += amount;
      } else {
        oneTimeCents += amount;
      }
    }
  });

  return {
    recurringCents,
    recurringTotals: Array.from(recurringTotalsByCadence.values()),
    oneTimeCents,
    currency,
    trialDays: trialDays ?? (recurringCents > 0 ? 7 : 0),
  };
};

const normalizeCouponCode = (code) =>
  typeof code === 'string' ? code.replace(/\s+/g, '').toUpperCase() : '';

const buildAppliedPromotionDescription = (promotion) => {
  if (!promotion) {
    return '';
  }

  if (promotion.discountType === 'percentage' && typeof promotion.discountValue === 'number') {
    return `${promotion.discountValue}% off`;
  }

  if (promotion.discountType === 'fixed_amount' && typeof promotion.discountValue === 'number') {
    return `${formatCurrency(promotion.discountValue)} off`;
  }

  if (promotion.discountPercent) {
    return `${promotion.discountPercent}% off`;
  }

  if (promotion.discountAmountCents) {
    return `${formatCurrency(promotion.discountAmountCents)} off`;
  }

  return promotion.description || 'Discount applied';
};

export const findSelectedCouponApplication = (catalogToCheck, cartItems, couponCode) => {
  const normalizedCouponCode = normalizeCouponCode(couponCode);
  if (!normalizedCouponCode || !Array.isArray(cartItems) || !cartItems.length) {
    return null;
  }

  const products = Array.isArray(catalogToCheck?.products) ? catalogToCheck.products : [];

  let selectedApplication = null;
  for (const item of cartItems) {
    if (!item?.productSlug) {
      return null;
    }

    const product = products.find((candidate) => candidate.slug === item.productSlug);
    if (!product || !Array.isArray(product.plans)) {
      return null;
    }

    const plan = product.plans.find((candidate) => candidate.planKey === item.planKey);
    const promotion = plan?.appliedPromotion || null;
    const promotionCodeMatches =
      promotion?.code && normalizeCouponCode(promotion.code) === normalizedCouponCode;
    const selectedPlanDiscounted =
      typeof plan?.amount === 'number' &&
      typeof plan?.discountedAmount === 'number' &&
      plan.discountedAmount < plan.amount;

    if (!promotionCodeMatches || !selectedPlanDiscounted) {
      return null;
    }

    selectedApplication = {
      product,
      plan,
      promotion,
    };
  }

  return selectedApplication;
};

const SavedPaymentMethods = ({
  methods,
  selectedId,
  onSelect,
  onRemove,
  onSetDefault,
  loading,
  pending,
  activeMode,
  onSwitchMode,
  resolving,
}) => {
  if (!methods.length) {
    return null;
  }

  const isSavedMode = activeMode === PAYMENT_MODES.SAVED;
  const isNewMode = activeMode === PAYMENT_MODES.NEW;
  const disableSavedToggle = isSavedMode || pending || resolving;
  const disableNewToggle = isNewMode || pending || resolving;

  return (
    <div className="saved-payment-methods">
      <div className="saved-payment-methods__header">
        <div className="saved-payment-methods__header-title">
          <FaCreditCard /> Saved cards
        </div>
        <div
          className="saved-payment-methods__tabs"
          role="tablist"
          aria-label="Payment method mode"
        >
          <button
            type="button"
            className={`saved-payment-methods__tab${
              isSavedMode ? ' saved-payment-methods__tab--active' : ''
            }`}
            onClick={() => onSwitchMode(PAYMENT_MODES.SAVED)}
            disabled={disableSavedToggle}
            aria-pressed={isSavedMode}
          >
            Use saved card
          </button>
          <button
            type="button"
            className={`saved-payment-methods__tab${
              isNewMode ? ' saved-payment-methods__tab--active' : ''
            }`}
            onClick={() => onSwitchMode(PAYMENT_MODES.NEW)}
            disabled={disableNewToggle}
            aria-pressed={isNewMode}
          >
            Use new card
          </button>
        </div>
      </div>

      {isSavedMode ? (
        <div className="saved-payment-methods__list">
          {methods.map((method) => {
            const label = `${method.brand ?? 'Card'} ending in ${method.last4 ?? '••••'}`;
            const expiry =
              method.expMonth && method.expYear
                ? `Expires ${method.expMonth}/${String(method.expYear).slice(-2)}`
                : '';
            const isSelected = selectedId === method.id;
            return (
              <label
                key={method.id}
                className={`saved-payment-methods__option${
                  isSelected ? ' saved-payment-methods__option--selected' : ''
                }`}
              >
                <input
                  type="radio"
                  name="savedPaymentMethod"
                  value={method.id}
                  checked={isSelected}
                  onChange={() => onSelect(method.id)}
                  disabled={pending || resolving}
                />
                <div className="saved-payment-methods__details">
                  <span className="saved-payment-methods__label">{label}</span>
                  <span className="saved-payment-methods__meta">
                    {expiry}
                    {method.isDefault ? ' • Default' : ''}
                  </span>
                </div>
                <div className="saved-payment-methods__actions">
                  {!method.isDefault ? (
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => onSetDefault(method.id)}
                      disabled={pending || resolving}
                    >
                      Make default
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="link-button link-button--danger"
                    onClick={() => onRemove(method.id)}
                    disabled={pending || resolving}
                  >
                    Remove
                  </button>
                </div>
              </label>
            );
          })}
        </div>
      ) : (
        <div className="saved-payment-methods__new-mode">
          <p>You chose to pay with a new card. Enter the details in the form below.</p>
          <button
            type="button"
            className="link-button"
            onClick={() => onSwitchMode(PAYMENT_MODES.SAVED)}
            disabled={pending || resolving}
          >
            Back to saved cards
          </button>
        </div>
      )}

      {loading || resolving ? (
        <div className="saved-payment-methods__loading">
          <Spinner animation="border" size="sm" />
          <span>{resolving ? 'Selecting your saved card…' : 'Refreshing cards…'}</span>
        </div>
      ) : null}
    </div>
  );
};

// Coupon code input and management section
const CouponCodeSection = ({
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  isValidating,
  validationError,
  disabled,
  inputValue,
  setInputValue,
  isExpanded,
  setIsExpanded,
}) => {
  const handleApply = () => {
    const normalizedInput = normalizeCouponCode(inputValue);
    if (normalizedInput && !isValidating && !disabled) {
      onApplyCoupon(normalizedInput);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    handleApply();
  };

  const handleRemove = () => {
    setInputValue('');
    onRemoveCoupon();
  };

  // If a coupon is applied, show the applied coupon UI
  if (appliedCoupon) {
    const isAutoApplied = appliedCoupon.isAutoApplied;
    return (
      <div className="coupon-code-section">
        <div className="coupon-code-applied">
          <div className="coupon-code-applied__info">
            <FaTag className="coupon-code-applied__icon" />
            <div className="coupon-code-applied__details">
              <span className="coupon-code-applied__code">{appliedCoupon.code}</span>
              {appliedCoupon.description && (
                <span className="coupon-code-applied__description">
                  {appliedCoupon.description}
                  {isAutoApplied && ' (auto-applied)'}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="coupon-code-applied__remove"
            onClick={handleRemove}
            disabled={disabled || isValidating}
            aria-label="Remove coupon"
          >
            <FaTimes />
          </button>
        </div>
      </div>
    );
  }

  // Show collapsed toggle or expanded input form
  return (
    <div className="coupon-code-section">
      {!isExpanded ? (
        <button
          type="button"
          className="coupon-code-toggle"
          onClick={() => setIsExpanded(true)}
          disabled={disabled}
        >
          <FaTag /> Have a promo code?
        </button>
      ) : (
        <div className="coupon-code-form">
          <div className="coupon-code-form__input-wrapper">
            <input
              type="text"
              className={`coupon-code-form__input${validationError ? ' coupon-code-form__input--error' : ''}`}
              placeholder="Enter promo code"
              aria-label="Promo code"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value.toUpperCase())}
              onKeyDown={handleKeyDown}
              disabled={disabled || isValidating}
              autoFocus
            />
            <button
              type="button"
              className="coupon-code-form__submit"
              disabled={!inputValue.trim() || isValidating || disabled}
              aria-label="Apply promo code"
              onClick={handleApply}
            >
              {isValidating ? <Spinner animation="border" size="sm" /> : <FaCheck />}
            </button>
          </div>
          {validationError && <div className="coupon-code-form__error">{validationError}</div>}
          <button
            type="button"
            className="coupon-code-form__cancel"
            onClick={() => {
              setIsExpanded(false);
              setInputValue('');
            }}
            disabled={isValidating}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

const CartSummary = ({
  items,
  totals,
  allowRemoval,
  onPlanChange,
  onRemove,
  metadata,
  title = 'Order summary',
  checkoutSucceeded = false,
  completedItems = [],
  onQuantityChange = () => {},
}) => {
  if (checkoutSucceeded) {
    const receiptItems = completedItems.length
      ? completedItems
      : items.map(({ raw, product, plan }) => ({
          key: `${raw.productSlug}-${raw.planKey}`,
          name:
            raw.productSlug === 'marketplace-strategy' && raw.metadata?.strategyName
              ? raw.metadata.strategyName
              : getCheckoutProductDisplayName(product),
          planLabel: plan?.label || plan?.planKey || raw.planKey,
          amount: typeof plan?.amount === 'number' ? plan.amount : null,
          currency: plan?.currency || null,
          interval: plan?.interval || null,
        }));

    return (
      <div className="checkout-summary-card checkout-summary-card--success">
        <h3>Purchase confirmed</h3>
        <p className="checkout-summary-success-copy">
          Access is unlocked for your purchase. We&rsquo;ll finish setup and continue automatically.
        </p>
        {receiptItems.length ? (
          <div className="checkout-summary-items">
            {receiptItems.map((item) => (
              <div key={item.key || item.name} className="checkout-summary-item">
                <div className="checkout-summary-item__header">
                  <div>
                    <div className="checkout-summary-item__name">{item.name}</div>
                    <div className="checkout-summary-item__plan">{item.planLabel}</div>
                  </div>
                </div>
                {typeof item.amount === 'number' ? (
                  <div className="checkout-summary-item__price">
                    {formatCurrency(item.amount, item.currency)}
                    {item.interval ? ` / ${item.interval}` : ''}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="checkout-summary-card">
        <h3>Your cart is empty</h3>
        <p>Add a product from one of the landing pages to get started.</p>
      </div>
    );
  }

  return (
    <div className="checkout-summary-card">
      <h3>{title}</h3>
      <div className="checkout-summary-items">
        {items.map(({ raw, product, plan, allPlans }) => {
          // For marketplace strategies, show the strategy name instead of the generic product name
          const isMarketplace = raw.productSlug === 'marketplace-strategy';
          const displayName =
            isMarketplace && raw.metadata?.strategyName
              ? raw.metadata.strategyName
              : getCheckoutProductDisplayName(product);
          const installmentDisclosure = buildInstallmentDisclosure(plan);
          const guaranteeDisclosure = buildGuaranteeDisclosure(product);

          return (
            <div key={`${raw.productSlug}-${raw.planKey}`} className="checkout-summary-item">
              <div className="checkout-summary-item__header">
                <div>
                  <div className="checkout-summary-item__name">{displayName}</div>
                  <div className="checkout-summary-item__plan">
                    {plan?.label || plan?.planKey || 'Plan unavailable'}
                  </div>
                </div>
                {allowRemoval ? (
                  <button
                    type="button"
                    className="link-button link-button--danger"
                    onClick={() =>
                      onRemove({
                        productSlug: raw.productSlug,
                        productName: displayName,
                        planLabel: plan?.label || plan?.planKey || 'Plan unavailable',
                      })
                    }
                  >
                    <FaTrashAlt /> Remove
                  </button>
                ) : null}
              </div>
              {/* Hide quantity for marketplace strategies. Quantity is always 1. */}
              {raw.productSlug !== 'marketplace-strategy' && (
                <div className="checkout-summary-item__quantity">
                  <label htmlFor={`quantity-${raw.productSlug}`}>Quantity</label>
                  <input
                    id={`quantity-${raw.productSlug}`}
                    type="number"
                    min={1}
                    max={10}
                    value={raw.quantity ?? 1}
                    onChange={(event) =>
                      onQuantityChange(
                        raw.productSlug,
                        raw.planKey,
                        Math.max(1, Math.min(10, Number(event.target.value) || 1)),
                      )
                    }
                  />
                </div>
              )}
              {allPlans.length > 1 ? (
                <div className="checkout-summary-item__plans">
                  <label htmlFor={`plan-select-${raw.productSlug}`}>Billing option</label>
                  <select
                    id={`plan-select-${raw.productSlug}`}
                    value={plan?.planKey || raw.planKey}
                    onChange={(event) => onPlanChange(raw.productSlug, event.target.value)}
                  >
                    {allPlans.map((option) => {
                      // Use discountedAmount when available (from promotions)
                      const displayAmount = option.discountedAmount ?? option.amount;
                      const hasDiscount =
                        typeof option.discountedAmount === 'number' &&
                        option.discountedAmount < option.amount;
                      return (
                        <option
                          key={option.planKey}
                          value={option.planKey}
                          disabled={option.checkoutAvailability?.purchasable === false}
                        >
                          {option.label || option.planKey}
                          {typeof displayAmount === 'number'
                            ? ` (${formatCurrency(displayAmount, option.currency)}${
                                option.interval ? `/${option.interval}` : ''
                              }${hasDiscount ? ` - was ${formatCurrency(option.amount, option.currency)}` : ''})`
                            : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
              ) : null}
              {typeof plan?.amount === 'number' ? (
                <div className="checkout-summary-item__price">
                  {/* Show discounted price when available, with original price struck through */}
                  {typeof plan.discountedAmount === 'number' &&
                  plan.discountedAmount < plan.amount ? (
                    <>
                      <span
                        style={{
                          textDecoration: 'line-through',
                          opacity: 0.6,
                          marginRight: '8px',
                        }}
                      >
                        {formatCurrency(plan.amount, plan.currency)}
                      </span>
                      <span style={{ color: '#22c55e', fontWeight: 600 }}>
                        {formatCurrency(plan.discountedAmount, plan.currency)}
                      </span>
                    </>
                  ) : (
                    formatCurrency(plan.amount, plan.currency)
                  )}
                  {plan.interval ? ` / ${plan.interval}` : ''}
                </div>
              ) : null}
              {plan?.metadata?.savingsText ? (
                <div className="checkout-summary-item__savings">{plan.metadata.savingsText}</div>
              ) : null}
              {installmentDisclosure ? (
                <div className="checkout-summary-item__installments">{installmentDisclosure}</div>
              ) : null}
              {guaranteeDisclosure ? (
                <div className="checkout-summary-item__guarantee">{guaranteeDisclosure}</div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="checkout-summary-totals">
        {totals.recurringTotals.map((recurringTotal) => (
          <div
            className="checkout-summary-total-line"
            key={`${recurringTotal.currency}-${recurringTotal.cadenceLabel}`}
          >
            <span>
              Recurring total
              {totals.recurringTotals.length > 1 ? ` · every ${recurringTotal.cadenceLabel}` : ''}
            </span>
            <span>
              {formatCurrency(recurringTotal.amountCents, recurringTotal.currency)} /{' '}
              {recurringTotal.cadenceLabel}
            </span>
          </div>
        ))}
        {totals.oneTimeCents > 0 ? (
          <div className="checkout-summary-total-line">
            <span>One-time total</span>
            <span>{formatCurrency(totals.oneTimeCents, totals.currency)}</span>
          </div>
        ) : null}
      </div>

      {metadata?.priceSummary ? (
        <div className="checkout-summary-note">
          <FaLock /> {metadata.priceSummary}
        </div>
      ) : null}
    </div>
  );
};

const CheckoutExperience = ({
  cart,
  catalog,
  catalogLoading,
  catalogError,
  refreshCatalog,
  cartLoading,
  cartPending,
  updatePlan,
  updateQuantity,
  removeItem,
  clearCart,
  savedMethods,
  flowContext,
  metadata,
  profile,
  checkoutTheme = 'dark',
  checkoutExperienceExperiment = null,
  couponState,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    refreshPaymentStatus,
    productSlugs: ownedProductSlugs = [],
    hasResolvedPaymentStatus = false,
    isLoading: paymentStatusLoading = false,
  } = usePaymentStatus();
  const {
    isAuthenticated,
    user,
    userProfile,
    rawAuthUser,
    getApiToken,
    profileLoading,
    handleCustomJwtAuth,
  } = useUser();

  const checkoutExperienceEventProps = useMemo(() => {
    const properties = {};
    if (checkoutExperienceExperiment?.enrolled)
      Object.assign(properties, {
        checkout_experience_experiment_name: ACTIVE_EXPERIMENTS.STRIPE_HOSTED_CHECKOUT_2026_08.name,
        checkout_experience_feature_flag:
          ACTIVE_EXPERIMENTS.STRIPE_HOSTED_CHECKOUT_2026_08.featureFlag,
        checkout_experience_variant: checkoutExperienceExperiment.variant,
        checkout_experience_subject_id: checkoutExperienceExperiment.subjectId,
        checkout_experience_assignment_id: checkoutExperienceExperiment.assignmentId,
        checkout_experience_surface: 'unified_cart_checkout',
        checkout_experience_exposed_at: checkoutExperienceExperiment.exposedAt,
        [`$feature/${ACTIVE_EXPERIMENTS.STRIPE_HOSTED_CHECKOUT_2026_08.featureFlag}`]:
          checkoutExperienceExperiment.variant,
      });
    return properties;
  }, [checkoutExperienceExperiment]);
  const checkoutMetadata = useMemo(
    () => ({
      ...(metadata || {}),
      ...checkoutExperienceEventProps,
    }),
    [checkoutExperienceEventProps, metadata],
  );

  useEffect(() => {
    if (!SHOULD_LOG_CHECKOUT_DEBUG) return;
    console.log('[CheckoutPage] Profile data:', {
      isAuthenticated,
      profileLoading,
      userProfile,
      rawAuthUser,
      hasFirstName: userProfile?.firstName,
      hasLastName: userProfile?.lastName,
      hasPhone: userProfile?.phone,
      hasEmail: userProfile?.email,
    });
  }, [isAuthenticated, profileLoading, rawAuthUser, userProfile]);

  const contactFromSession = useMemo(() => loadContactFromSession(), []);

  const queryPrefill = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      name: params.get('name') || contactFromSession?.name || '',
      email: params.get('email') || contactFromSession?.email || '',
      phone: params.get('phone') || contactFromSession?.phone || '',
    };
  }, [contactFromSession, location.search]);

  const [contact, setContact] = useState(() => ({
    name: queryPrefill.name,
    email: queryPrefill.email,
    phone: queryPrefill.phone,
    howFoundUs: '',
    howFoundUsOtherText: '',
  }));

  const profileSource = userProfile || {};
  const rawSource = rawAuthUser || {};

  const profileFirstName =
    typeof profileSource.firstName === 'string' && profileSource.firstName.trim()
      ? profileSource.firstName.trim()
      : typeof rawSource?.given_name === 'string' && rawSource.given_name.trim()
        ? rawSource.given_name.trim()
        : '';
  const profileLastName =
    typeof profileSource.lastName === 'string' && profileSource.lastName.trim()
      ? profileSource.lastName.trim()
      : typeof rawSource?.family_name === 'string' && rawSource.family_name.trim()
        ? rawSource.family_name.trim()
        : '';
  const profileNickname =
    typeof profileSource.nickname === 'string' && profileSource.nickname.trim()
      ? profileSource.nickname.trim()
      : typeof rawSource?.nickname === 'string' && rawSource.nickname.trim()
        ? rawSource.nickname.trim()
        : '';
  const profileEmail =
    typeof profileSource.email === 'string' && profileSource.email.trim()
      ? profileSource.email.trim()
      : typeof rawSource?.email === 'string' && rawSource.email.trim()
        ? rawSource.email.trim()
        : '';
  const profilePhone =
    typeof profileSource.phone === 'string' && profileSource.phone.trim()
      ? profileSource.phone.trim()
      : typeof rawSource?.phone === 'string' && rawSource.phone.trim()
        ? rawSource.phone.trim()
        : typeof rawSource?.phone_number === 'string' && rawSource.phone_number.trim()
          ? rawSource.phone_number.trim()
          : '';

  const profileNameCandidates = [
    [profileFirstName, profileLastName].filter(Boolean).join(' ').trim(),
    typeof profileSource.name === 'string' && profileSource.name.trim()
      ? profileSource.name.trim()
      : '',
    typeof profileSource.fullName === 'string' && profileSource.fullName.trim()
      ? profileSource.fullName.trim()
      : '',
    `${profileSource.firstName ?? ''} ${profileSource.lastName ?? ''}`.trim(),
    profileNickname,
    typeof rawSource?.name === 'string' && rawSource.name.trim() ? rawSource.name.trim() : '',
  ]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter((value) => value && !value.toLowerCase().includes('@'));

  const fallbackNameFromEmail = profileEmail
    ? profileEmail
        .split('@')[0]
        .replace(/[._-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    : '';

  const resolvedProfileName =
    profileNameCandidates[0] ||
    (fallbackNameFromEmail ? fallbackNameFromEmail.replace(/\b\w/g, (ch) => ch.toUpperCase()) : '');

  const hasProfileIdentity = isAuthenticated && (resolvedProfileName || profileEmail);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [smsConsentAccepted, setSmsConsentAccepted] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setErrorRaw] = useState('');
  const [checkoutToast, setCheckoutToast] = useState({
    show: false,
    msg: '',
    variant: 'info',
  });
  const [hostedCheckoutStatus, setHostedCheckoutStatus] = useState('idle');
  const hostedCheckoutSessionRef = useRef(null);
  const popToast = useCallback(
    (msg, variant = 'info') => setCheckoutToast({ show: true, msg, variant }),
    [],
  );
  // Wrap setError so every payment error also fires a visible toast
  const setError = useCallback(
    (msg) => {
      setErrorRaw(msg);
      if (msg) popToast(msg, 'danger');
    },
    [popToast],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (!sessionId || hostedCheckoutStatus !== 'idle') {
      return undefined;
    }
    if (hostedCheckoutSessionRef.current === sessionId) {
      return undefined;
    }
    hostedCheckoutSessionRef.current = sessionId;

    let mounted = true;
    const finalizeHostedCheckout = async () => {
      setHostedCheckoutStatus('processing');
      try {
        const redirectTo = params.get('redirectTo') || '/account-settings/subscriptions';
        if (!mounted) return;

        setHostedCheckoutStatus('complete');
        navigate(redirectTo, { replace: true });
      } catch (hostedError) {
        console.error('[CheckoutPage] Hosted checkout finalization failed', hostedError);
        if (mounted) {
          setHostedCheckoutStatus('error');
          setError(
            'Payment completed, but account setup needs a refresh. Please check your email or contact support.',
          );
        }
      }
    };

    finalizeHostedCheckout();
    return () => {
      mounted = false;
    };
  }, [hostedCheckoutStatus, location.search, navigate, setError]);

  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const [paymentElementTimedOut, setPaymentElementTimedOut] = useState(false);
  const [selectedSavedPaymentMethod, setSelectedSavedPaymentMethod] = useState(
    INITIAL_SAVED_PAYMENT_METHOD,
  );
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.SAVED);
  const [pendingRemovalItem, setPendingRemovalItem] = useState(null);
  const [removingItem, setRemovingItem] = useState(false);
  const [pendingCardRemovalId, setPendingCardRemovalId] = useState(null);
  const [removingCard, setRemovingCard] = useState(false);

  // Coupon state lives above the keyed Stripe Elements boundary so a catalog
  // refresh cannot collapse a rejected code or discard an applied discount.
  const {
    appliedCoupon,
    setAppliedCoupon,
    isValidatingCoupon,
    setIsValidatingCoupon,
    couponValidationError,
    setCouponValidationError,
    catalogWithCoupon,
    setCatalogWithCoupon,
    dismissedAutoApplyCoupon,
    setDismissedAutoApplyCoupon,
    couponCodeInput,
    setCouponCodeInput,
    couponCodeExpanded,
    setCouponCodeExpanded,
  } = couponState;

  const findPreferredSavedMethod = useCallback(() => {
    if (!savedMethods.hasMethods) {
      return null;
    }
    return (
      savedMethods.paymentMethods.find((method) => method.isDefault) ||
      savedMethods.paymentMethods[0] ||
      null
    );
  }, [savedMethods.hasMethods, savedMethods.paymentMethods]);
  const [catalogBlockingError, setCatalogBlockingError] = useState('');
  const catalogIssueKeyRef = useRef('');

  useEffect(() => {
    if (!hasProfileIdentity) {
      return;
    }

    if (!resolvedProfileName && !profileEmail && !profilePhone) {
      return;
    }

    setContact((prev) => {
      const nextName = resolvedProfileName || prev.name || '';
      const nextEmail = profileEmail || prev.email || '';
      const nextPhone = profilePhone || prev.phone || '';

      const sanitizedName =
        nextName && nextEmail && nextName.toLowerCase() === nextEmail.toLowerCase()
          ? resolvedProfileName || nextName
          : nextName;

      if (sanitizedName === prev.name && nextEmail === prev.email && nextPhone === prev.phone) {
        return prev;
      }

      return {
        ...prev,
        name: sanitizedName,
        email: nextEmail,
        phone: nextPhone,
      };
    });
  }, [hasProfileIdentity, profileEmail, profilePhone, resolvedProfileName]);

  // Use catalog with coupon discounts when a coupon is applied
  // If user dismissed the auto-applied coupon, strip discounts from the base catalog
  const effectiveCatalog = useMemo(() => {
    if (catalogWithCoupon) {
      return catalogWithCoupon;
    }

    // If user dismissed auto-apply coupon, strip discountedAmount from all plans
    if (dismissedAutoApplyCoupon && catalog?.products) {
      return {
        ...catalog,
        products: catalog.products.map((product) => ({
          ...product,
          plans: product.plans?.map((plan) => {
            // Remove discount-related fields to show original prices
            const { discountedAmount, appliedPromotion, ...planWithoutDiscount } = plan;
            return planWithoutDiscount;
          }),
        })),
      };
    }

    return catalog;
  }, [catalogWithCoupon, catalog, dismissedAutoApplyCoupon]);

  const { items: derivedItems, missing: missingCatalogItems } = useMemo(
    () => deriveCartItems(cart, effectiveCatalog, metadata),
    [cart, effectiveCatalog, metadata],
  );
  const alreadyOwnedItems = useMemo(() => {
    if (
      !isAuthenticated ||
      !hasResolvedPaymentStatus ||
      paymentStatusLoading ||
      !Array.isArray(ownedProductSlugs)
    ) {
      return [];
    }

    const owned = new Set(ownedProductSlugs.filter((slug) => typeof slug === 'string'));
    return derivedItems.filter(({ raw }) => owned.has(raw?.productSlug));
  }, [
    derivedItems,
    hasResolvedPaymentStatus,
    isAuthenticated,
    ownedProductSlugs,
    paymentStatusLoading,
  ]);
  const derivedItemsLength = derivedItems.length;
  const totals = useMemo(() => computeTotals(derivedItems), [derivedItems]);

  useEffect(() => {
    if (!SHOULD_LOG_CHECKOUT_DEBUG) return;
    if (!derivedItems.length) return;

    console.log('[CheckoutPage] 💰 PRICING DEBUG:', {
      appliedCouponState: appliedCoupon,
      catalogWithCouponActive: !!catalogWithCoupon,
      cartMetadataPromoCode: metadata?.promoCode,
      cartFlowContext: flowContext,
    });

    derivedItems.forEach(({ product, plan, raw }) => {
      const hasDiscount =
        typeof plan?.discountedAmount === 'number' && plan.discountedAmount < plan.amount;
      console.log(`[CheckoutPage] 📦 Product: ${product?.name || raw?.productSlug}`, {
        planKey: plan?.planKey,
        originalAmount: plan?.amount,
        discountedAmount: plan?.discountedAmount,
        hasDiscount,
        appliedPromotion: plan?.appliedPromotion,
        planMetadata: plan?.metadata,
      });
    });

    // Also log the raw catalog to see if discounts are coming from there
    const catalogProducts = effectiveCatalog?.products || [];
    catalogProducts.forEach((product) => {
      product.plans?.forEach((plan) => {
        if (typeof plan.discountedAmount === 'number') {
          console.log(
            `[CheckoutPage] 📋 Catalog has discount for ${product.slug}/${plan.planKey}:`,
            {
              amount: plan.amount,
              discountedAmount: plan.discountedAmount,
              appliedPromotion: plan.appliedPromotion,
            },
          );
        }
      });
    });
  }, [derivedItems, appliedCoupon, catalogWithCoupon, metadata, flowContext, effectiveCatalog]);

  // Detect pre-applied promotions from the catalog (auto-apply promotions)
  // and display them in the coupon code section
  useEffect(() => {
    // Skip if user explicitly dismissed the auto-applied coupon
    if (dismissedAutoApplyCoupon) return;
    // Skip if we already have a manually applied coupon or catalog with coupon
    if (appliedCoupon || catalogWithCoupon) return;
    if (!derivedItems.length) return;

    // Only carry an automatic promotion when the catalog proves that the same
    // promotion applies to every selected plan. A partial mixed-cart promotion
    // would otherwise be sent to Stripe for an ineligible product and reject
    // the entire purchase.
    for (const { plan } of derivedItems) {
      if (plan?.appliedPromotion?.code) {
        const promo = plan.appliedPromotion;
        const selectedApplication = findSelectedCouponApplication(catalog, cart?.items, promo.code);
        if (!selectedApplication) {
          setDismissedAutoApplyCoupon(true);
          return;
        }
        const description = buildAppliedPromotionDescription(promo);

        if (SHOULD_LOG_CHECKOUT_DEBUG) {
          console.log('[CheckoutPage] 🎫 Detected auto-applied promotion:', promo);
        }
        setAppliedCoupon({
          code: promo.code,
          description: description || 'Discount applied',
          isAutoApplied: true, // Flag to indicate this was auto-applied
        });
        break;
      }
    }
  }, [
    derivedItems,
    appliedCoupon,
    catalogWithCoupon,
    dismissedAutoApplyCoupon,
    catalog,
    cart?.items,
    setDismissedAutoApplyCoupon,
  ]);
  const originMetadata = metadata?.origin || null;
  const originReturnUrl = originMetadata?.returnUrl || null;
  const contextBannerMessage = useMemo(() => {
    if (!profile || !profile.banner) {
      return null;
    }
    if (typeof profile.banner === 'function') {
      return profile.banner(metadata);
    }
    return profile.banner;
  }, [metadata, profile]);
  const removalModalMessage = useMemo(() => {
    if (!pendingRemovalItem) {
      return '';
    }
    const { productName, planLabel } = pendingRemovalItem;
    if (productName && planLabel) {
      return `Remove ${productName} (${planLabel}) from your cart?`;
    }
    if (productName) {
      return `Remove ${productName} from your cart?`;
    }
    return 'Remove this item from your cart?';
  }, [pendingRemovalItem]);
  const termsAdditionalText = profile?.termsAdditionalText ?? 'and authorize this purchase.';
  const termsAcceptanceError =
    profile?.termsAcceptanceError ?? 'Please accept the Terms of Service to continue.';
  const ctaLabel = profile?.ctaLabel ?? 'Complete Purchase';
  const ctaProcessingLabel = profile?.ctaProcessingLabel ?? 'Processing your payment…';
  const ctaSparkle = profile?.ctaSparkle ?? false;
  const reassuranceItems = profile?.reassuranceItems ?? [];
  const paymentHighlights = profile?.paymentHighlights ?? [];

  useEffect(() => {
    if (catalogLoading) {
      return;
    }

    const hasMissing = missingCatalogItems.length > 0;
    const missingDetails = hasMissing
      ? missingCatalogItems.map((entry) => `${entry.productSlug}:${entry.planKey}:${entry.reason}`)
      : [];
    const missingKey = hasMissing ? missingDetails.slice().sort().join('|') : '';
    const shouldBlock = hasMissing || (catalogError && derivedItemsLength > 0);
    const checkoutUnavailable = missingCatalogItems.find(
      (entry) => entry.reason === 'checkout_unavailable',
    );
    const missingCatalogData = missingCatalogItems.filter(
      (entry) => entry.reason !== 'checkout_unavailable',
    );

    if (shouldBlock) {
      setCatalogBlockingError(
        checkoutUnavailable?.message ||
          'Pricing data is missing for items in your cart. Please refresh the page or contact support.',
      );
      if (missingCatalogData.length && catalogIssueKeyRef.current !== missingKey) {
        const uniqueSlugs = Array.from(
          new Set(missingCatalogData.map((entry) => entry.productSlug)),
        ).join(',');
        pushEvent({
          event: ANALYTICS_EVENTS.CHECKOUT_CATALOG_MISSING_PRODUCT,
          missingSlugs: uniqueSlugs,
          flow_context: flowContext || null,
          cart_item_count: Array.isArray(cart?.items) ? cart.items.length : 0,
        });
        catalogIssueKeyRef.current = missingKey;
      }
    } else {
      setCatalogBlockingError('');
      catalogIssueKeyRef.current = '';
    }
  }, [catalogError, catalogLoading, cart, derivedItemsLength, flowContext, missingCatalogItems]);

  useEffect(() => {
    if (hasProfileIdentity) {
      return;
    }
    saveContactToSession(contact);
  }, [contact, hasProfileIdentity]);

  const hasSelectedSavedPaymentMethod =
    typeof selectedSavedPaymentMethod === 'string' && selectedSavedPaymentMethod.length > 0;
  const selectedSavedPaymentMethodId = hasSelectedSavedPaymentMethod
    ? selectedSavedPaymentMethod
    : null;
  const isResolvingSavedPaymentMethod = selectedSavedPaymentMethod === INITIAL_SAVED_PAYMENT_METHOD;
  const usingSavedCard = paymentMode === PAYMENT_MODES.SAVED && hasSelectedSavedPaymentMethod;
  const usingNewCard =
    paymentMode === PAYMENT_MODES.NEW || (!usingSavedCard && !isResolvingSavedPaymentMethod);

  useEffect(() => {
    if (!savedMethods.hasMethods) {
      if (!savedMethods.loading && selectedSavedPaymentMethod !== null) {
        setSelectedSavedPaymentMethod(null);
      }
      if (paymentMode !== PAYMENT_MODES.NEW) {
        setPaymentMode(PAYMENT_MODES.NEW);
      }
      return;
    }

    if (selectedSavedPaymentMethod === INITIAL_SAVED_PAYMENT_METHOD) {
      const preferred = findPreferredSavedMethod();
      if (preferred) {
        setSelectedSavedPaymentMethod(preferred.id);
        if (paymentMode !== PAYMENT_MODES.SAVED) {
          setPaymentMode(PAYMENT_MODES.SAVED);
        }
      } else {
        setSelectedSavedPaymentMethod(null);
        if (paymentMode !== PAYMENT_MODES.NEW) {
          setPaymentMode(PAYMENT_MODES.NEW);
        }
      }
      return;
    }

    if (
      hasSelectedSavedPaymentMethod &&
      !savedMethods.paymentMethods.some((method) => method.id === selectedSavedPaymentMethod)
    ) {
      // Existing selection vanished (e.g., card removed from another session); re-run auto selection.
      setSelectedSavedPaymentMethod(INITIAL_SAVED_PAYMENT_METHOD);
      return;
    }

    if (hasSelectedSavedPaymentMethod) {
      if (paymentMode !== PAYMENT_MODES.SAVED) {
        setPaymentMode(PAYMENT_MODES.SAVED);
      }
    } else if (!isResolvingSavedPaymentMethod && paymentMode !== PAYMENT_MODES.NEW) {
      setPaymentMode(PAYMENT_MODES.NEW);
    }
  }, [
    findPreferredSavedMethod,
    hasSelectedSavedPaymentMethod,
    isResolvingSavedPaymentMethod,
    paymentMode,
    savedMethods.hasMethods,
    savedMethods.loading,
    savedMethods.paymentMethods,
    selectedSavedPaymentMethod,
  ]);

  const handleContactChange = useCallback((field, value) => {
    setContact((prev) => ({ ...prev, [field]: value }));
    if (field === 'phone') {
      // Consent proof is scoped to the exact phone number beside the disclosure.
      setSmsConsentAccepted(false);
    }
  }, []);

  // Coupon code handlers
  const handleApplyCoupon = useCallback(
    async (code) => {
      if (!code || isValidatingCoupon || processing) return;

      setIsValidatingCoupon(true);
      setCouponValidationError('');

      try {
        const token = isAuthenticated ? await getApiToken() : null;
        const updatedCatalog = await validatePromoCode(code, token);
        const couponApplication = findSelectedCouponApplication(updatedCatalog, cart?.items, code);

        if (couponApplication) {
          const promotionInfo = couponApplication.promotion;
          setAppliedCoupon({
            code: normalizeCouponCode(code),
            description: buildAppliedPromotionDescription(promotionInfo),
          });
          setCatalogWithCoupon(updatedCatalog);
          setCouponValidationError('');
        } else {
          const selectedProductNames = derivedItems
            .map(({ product, raw }) => product?.name || raw?.productSlug)
            .filter(Boolean);
          const productLabel =
            selectedProductNames.length === 1 ? selectedProductNames[0] : 'the selected items';
          setAppliedCoupon(null);
          setCatalogWithCoupon(null);
          // A code can be perfectly valid for some other product. The API then
          // says "applied" while nothing in this cart changed price; telling the
          // buyer "Promo code applied." at list price is a lie (PRO50OFF on the
          // Bootcamp, 2026-09-11). Only reuse the API's wording for a real
          // rejection (expired, not found, inactive, not started).
          const validation = updatedCatalog?.promotionCodeValidation;
          const validationMessage =
            validation && validation.status !== 'applied' ? validation.message : null;
          setCouponValidationError(
            validationMessage || `This promo code did not work for ${productLabel}.`,
          );
        }
      } catch (couponError) {
        console.error('[CheckoutPage] Coupon validation failed:', couponError);
        setCouponValidationError(
          couponError?.message || 'Failed to validate promo code. Please try again.',
        );
      } finally {
        setIsValidatingCoupon(false);
      }
    },
    [cart?.items, derivedItems, getApiToken, isAuthenticated, isValidatingCoupon, processing],
  );

  const handleRemoveCoupon = useCallback(() => {
    // If removing an auto-applied coupon, mark it as dismissed to prevent re-detection
    if (appliedCoupon?.isAutoApplied) {
      setDismissedAutoApplyCoupon(true);
    }
    setAppliedCoupon(null);
    setCatalogWithCoupon(null);
    setCouponValidationError('');
  }, [appliedCoupon?.isAutoApplied]);

  const handleSelectSavedPaymentMethod = useCallback((id) => {
    setSelectedSavedPaymentMethod(id);
    setPaymentMode(PAYMENT_MODES.SAVED);
  }, []);

  const handleSwitchPaymentMode = useCallback(
    (mode) => {
      if (mode === paymentMode || isResolvingSavedPaymentMethod) {
        return;
      }

      if (mode === PAYMENT_MODES.SAVED) {
        const preferred =
          hasSelectedSavedPaymentMethod && selectedSavedPaymentMethodId
            ? savedMethods.paymentMethods.find(
                (method) => method.id === selectedSavedPaymentMethodId,
              )
            : findPreferredSavedMethod();
        if (!preferred) {
          return;
        }
        setSelectedSavedPaymentMethod(preferred.id);
        setPaymentMode(PAYMENT_MODES.SAVED);
      } else {
        setSelectedSavedPaymentMethod(null);
        setPaymentMode(PAYMENT_MODES.NEW);
      }
    },
    [
      findPreferredSavedMethod,
      hasSelectedSavedPaymentMethod,
      paymentMode,
      isResolvingSavedPaymentMethod,
      savedMethods.paymentMethods,
      selectedSavedPaymentMethodId,
    ],
  );

  const allowRemoval = useMemo(() => !RESTRICT_REMOVE_FLOWS.has(flowContext || ''), [flowContext]);

  const redirectTargetFromCart = useMemo(() => {
    if (metadata?.redirectTarget) {
      return { ...metadata.redirectTarget };
    }

    if (originReturnUrl) {
      return { type: 'custom', url: originReturnUrl };
    }

    return { type: 'dashboard' };
  }, [metadata?.redirectTarget, originReturnUrl]);

  const leadTransactionId = metadata?.leadTransactionId || null;

  const utmData = useMemo(() => getUtmParamsForApi(), []);
  const attributionParams = useMemo(() => getAttributionParams(utmData), [utmData]);

  /**
   * Begin checkout: the buyer reached the unified cart checkout. Fired on
   * arrival, not on the marketing-page button click, so it means "reached
   * checkout". Held until the catalog resolves every cart line, so the plan and
   * value reported are the real ones rather than an empty pre-fetch snapshot.
   * `trackBeginCheckout` keys the guard on surface, products, plans and
   * experiment arm, so re-render, tab refocus and the checkout overlay opening
   * and closing all report exactly one conversion per checkout session.
   */
  useEffect(() => {
    if (!derivedItems.length || missingCatalogItems.length) return;
    const planKeys = derivedItems.map(
      (item) => item.plan?.planKey || item.raw.planKey || 'default',
    );
    const productSlugs = derivedItems.map(
      (item) => item.product?.slug || item.raw.productSlug || 'unknown',
    );
    trackBeginCheckout({
      dedupeKey: [
        'unified_cart_checkout',
        productSlugs.join('|'),
        planKeys.join('|'),
        checkoutExperienceEventProps.checkout_experience_variant || 'control',
      ].join(':'),
      source: location.pathname,
      checkout_surface: 'unified_cart_checkout',
      user_type: isAuthenticated ? 'registered' : 'guest',
      plan_type: productSlugs.join(','),
      plan_key: planKeys.join(','),
      cart_item_count: derivedItems.length,
      flow_context: flowContext || null,
      lead_transaction_id: leadTransactionId,
      // Not the amount charged today. The ad platforms rank conversions by
      // value, so an installment plan counts every installment and an open
      // ended subscription counts its assumed tenure. `totals` is untouched
      // because the page still has to display the real payment schedule.
      // See utils/checkoutAnalyticsValue.ts.
      value: checkoutAnalyticsValue(derivedItems),
      currency: totals.currency,
      ...checkoutExperienceEventProps,
      ...utmData,
    });
  }, [
    checkoutExperienceEventProps,
    derivedItems,
    flowContext,
    isAuthenticated,
    leadTransactionId,
    location.pathname,
    missingCatalogItems.length,
    totals,
    utmData,
  ]);

  const billingContact = useMemo(() => {
    if (hasProfileIdentity) {
      return {
        name: resolvedProfileName || contact.name || '',
        email: profileEmail || contact.email || '',
        phone: profilePhone || contact.phone || '',
      };
    }
    return contact;
  }, [contact, hasProfileIdentity, profileEmail, profilePhone, resolvedProfileName]);

  const [checkoutSucceeded, setCheckoutSucceeded] = useState(false);
  const [completedItems, setCompletedItems] = useState([]);

  const cartHasItems = derivedItemsLength > 0;
  const paymentElementMountKey = useMemo(
    () =>
      derivedItems
        .map(({ raw, plan }) =>
          [
            raw.productSlug,
            raw.planKey,
            raw.quantity ?? 1,
            plan?.amount ?? '',
            plan?.discountedAmount ?? '',
            plan?.interval ?? '',
          ].join(':'),
        )
        .join('|'),
    [derivedItems],
  );
  const shouldRenderPaymentElement = cartHasItems && !catalogBlockingError && !checkoutSucceeded;
  const howFoundUsComplete =
    contact.howFoundUs && (contact.howFoundUs !== 'other' || contact.howFoundUsOtherText?.trim());
  const contactIdentityFieldsComplete = hasProfileIdentity
    ? Boolean(billingContact.name && billingContact.email)
    : Boolean(contact.name.trim() && contact.email.trim() && contact.phone.trim());
  const contactFieldsComplete = Boolean(contactIdentityFieldsComplete && howFoundUsComplete);
  const contactComplete =
    (contactFieldsComplete && cartHasItems && !catalogBlockingError) || checkoutSucceeded;
  const contactWasPrefilledOnLoad = Boolean(
    queryPrefill.name && queryPrefill.email && queryPrefill.phone,
  );

  const paymentElementOptions = useMemo(
    () => ({
      layout: 'accordion',
      // Hide Stripe's internal terms text since we have our own Terms of Service checkbox
      // This prevents the "By providing your card..." text from being cut off on mobile
      terms: {
        card: 'never',
      },
      wallets: {
        applePay: 'never',
        googlePay: 'never',
      },
      defaultValues: {
        billingDetails: {
          name: billingContact.name || undefined,
          email: billingContact.email || undefined,
          phone: billingContact.phone || undefined,
        },
      },
    }),
    [billingContact],
  );

  useEffect(() => {
    setPaymentElementReady(false);
    setPaymentElementTimedOut(false);
  }, [paymentElementMountKey]);

  useEffect(() => {
    if (!shouldRenderPaymentElement || usingSavedCard || paymentElementReady || checkoutSucceeded) {
      setPaymentElementTimedOut(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setPaymentElementTimedOut(true);
    }, 8000);

    return () => window.clearTimeout(timeoutId);
  }, [checkoutSucceeded, paymentElementReady, shouldRenderPaymentElement, usingSavedCard]);

  const buildSelectedPlanPayload = useCallback(() => {
    if (!cart?.items?.length) {
      return [];
    }
    return cart.items.map((item) => ({
      productSlug: item.productSlug,
      planKey: item.planKey || null,
      quantity: item.quantity ?? 1,
    }));
  }, [cart]);

  const handlePlanChange = useCallback(
    async (productSlug, planKey) => {
      try {
        await updatePlan(productSlug, planKey);
      } catch (planError) {
        console.error('[CheckoutPage] Failed to update plan', planError);
        setError(planError?.message || 'Failed to update plan. Please try again.');
      }
    },
    [setError, updatePlan],
  );

  const handleQuantityChange = useCallback(
    async (productSlug, planKey, nextQuantity) => {
      const safeQuantity = Math.max(1, Math.min(10, Math.floor(Number(nextQuantity) || 1)));
      try {
        await updateQuantity(productSlug, { quantity: safeQuantity, planKey });
      } catch (quantityError) {
        console.error('[CheckoutPage] Failed to update quantity', quantityError);
        setError(quantityError?.message || 'Failed to update quantity. Please try again.');
      }
    },
    [setError, updateQuantity],
  );

  const requestRemoveItem = useCallback((item) => {
    if (!item || !item.productSlug) {
      return;
    }
    setPendingRemovalItem(item);
  }, []);

  const closeRemoveItemModal = useCallback(() => {
    if (removingItem) {
      return;
    }
    setPendingRemovalItem(null);
  }, [removingItem]);

  const confirmRemoveItem = useCallback(async () => {
    if (!pendingRemovalItem || !pendingRemovalItem.productSlug) {
      return;
    }
    setRemovingItem(true);
    try {
      await removeItem(pendingRemovalItem.productSlug);
      setPendingRemovalItem(null);
    } catch (removeError) {
      console.error('[CheckoutPage] Failed to remove item', removeError);
      setError(removeError?.message || 'Failed to remove item.');
    } finally {
      setRemovingItem(false);
    }
  }, [pendingRemovalItem, removeItem, setError]);

  const buildRedirectUrl = useCallback(() => {
    if (originReturnUrl) {
      return originReturnUrl;
    }
    if (redirectTargetFromCart?.type === 'starter_plan') {
      // Redirect to AI Agent instead of deprecated AI Bot Builder
      // The handoff is already set via setAgentHandoff() and will be picked up by AI Agent
      const params = new URLSearchParams();
      if (leadTransactionId) {
        params.set('leadId', leadTransactionId);
      }
      return `/ai-agent?${params.toString()}`;
    }
    return null;
  }, [leadTransactionId, originReturnUrl, redirectTargetFromCart]);

  const buildPostCheckoutSurveyUrl = useCallback(
    (redirectTo, conversionResult = {}) => {
      const firstItem = Array.isArray(cart?.items) ? cart.items[0] : null;
      if (
        Array.isArray(cart?.items) &&
        cart.items.some((item) => isStarterOrLightProduct(item?.productSlug))
      ) {
        return buildPostPurchaseChallengePath(
          redirectTo || '/account-settings/subscriptions',
          'unified_starter_light_checkout_post_purchase',
        );
      }
      const surveyParams = new URLSearchParams();
      if (billingContact.email) surveyParams.set('email', billingContact.email);
      if (billingContact.phone) surveyParams.set('phone', billingContact.phone);
      if (billingContact.name) surveyParams.set('name', billingContact.name);
      if (conversionResult.customerId) surveyParams.set('customerId', conversionResult.customerId);
      if (firstItem?.productSlug) surveyParams.set('productSlug', firstItem.productSlug);
      if (firstItem?.planKey) surveyParams.set('planKey', firstItem.planKey);
      surveyParams.set('purchase_success', 'true');
      surveyParams.set('source_flow', 'unified_checkout_post_purchase');
      surveyParams.set('redirectTo', redirectTo || '/account-settings/subscriptions');
      return `/signup-survey?${surveyParams.toString()}`;
    },
    [billingContact.email, billingContact.name, billingContact.phone, cart?.items],
  );

  const buildHostedCheckoutUrl = useCallback(() => {
    if (!API_URL || !cart?.items?.length) {
      return null;
    }

    const target = new URL(`${API_URL}/stripe/create-checkout-session`);
    const items = cart.items
      .filter((item) => item?.productSlug)
      .map((item) => ({
        productSlug: item.productSlug,
        planKey: item.planKey || null,
        quantity: item.quantity ?? 1,
      }));
    const productSlugs = items.map((item) => item.productSlug);
    const redirectTo = buildPostCheckoutSurveyUrl(
      buildRedirectUrl() || '/account-settings/subscriptions',
    );
    const successUrl = new URL('/checkout', window.location.origin);
    successUrl.searchParams.set('hosted_checkout_success', 'true');
    successUrl.searchParams.set('redirectTo', redirectTo);
    if (billingContact.email) {
      successUrl.searchParams.set('email', billingContact.email);
    }

    target.searchParams.set('items', JSON.stringify(items));
    target.searchParams.set('productSlugs', productSlugs.join(','));
    target.searchParams.set('successUrl', successUrl.toString());

    if (billingContact.email) target.searchParams.set('email', billingContact.email);
    if (billingContact.phone) target.searchParams.set('phoneNumber', billingContact.phone);
    if (appliedCoupon?.code) target.searchParams.set('promoCode', appliedCoupon.code);

    Object.entries(utmData || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim()) {
        target.searchParams.set(key, String(value));
      }
    });

    return target.toString();
  }, [
    appliedCoupon?.code,
    billingContact.email,
    billingContact.phone,
    buildPostCheckoutSurveyUrl,
    buildRedirectUrl,
    cart?.items,
    utmData,
  ]);

  const openHostedCheckout = useCallback(() => {
    const hostedUrl = buildHostedCheckoutUrl();
    if (!hostedUrl) {
      setError('Unable to open secure checkout. Please refresh and try again.');
      return;
    }

    pushEvent({
      event: ANALYTICS_EVENTS.DIRECT_FALLBACK_CHECKOUT,
      source: location.pathname,
      flow_context: flowContext || null,
      cart_item_count: Array.isArray(cart?.items) ? cart.items.length : 0,
      ...checkoutExperienceEventProps,
    });
    window.location.href = hostedUrl;
  }, [
    buildHostedCheckoutUrl,
    cart?.items,
    checkoutExperienceEventProps,
    flowContext,
    location.pathname,
    setError,
  ]);

  const maybeConfirmInvoicePayment = useCallback(
    async (conversionResult) => {
      if (!conversionResult?.requiresAction || !conversionResult?.invoiceClientSecret) {
        return;
      }
      if (!stripe) {
        throw new Error('Payment processor is not ready to finalize your payment.');
      }

      const { error: invoiceError } = await confirmPaymentWithClient(stripe, null, {
        clientSecret: conversionResult.invoiceClientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: buildRedirectUrl() || window.location.href,
        },
      });

      if (invoiceError) {
        throw new Error(
          invoiceError.message || 'Additional payment confirmation failed. Please try again.',
        );
      }

      refreshPaymentStatus({ forceRefresh: true });
    },
    [buildRedirectUrl, refreshPaymentStatus, stripe],
  );

  const submitPayment = useCallback(
    async (event) => {
      event.preventDefault();
      if (processing) {
        return;
      }

      setError('');

      if (!cartHasItems) {
        setError('Your cart is empty. Please add a plan before checking out.');
        setProcessing(false);
        return;
      }

      if (!contactFieldsComplete) {
        if (!howFoundUsComplete) {
          setError('Please tell us how you found BotSpot before completing your purchase.');
          return;
        }
        setError(
          hasProfileIdentity
            ? 'Unable to resolve your billing details. Please refresh and try again.'
            : 'Please complete your contact information before continuing.',
        );
        return;
      }

      if (!termsAccepted) {
        setError(termsAcceptanceError);
        setTermsError('You must accept the Terms of Service to continue');

        // Scroll to terms checkbox and add error styling
        setTimeout(() => {
          const termsGroup = document.querySelector('.terms-checkbox-group');
          if (termsGroup) {
            termsGroup.classList.add('error');
            termsGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);

        return;
      }

      if (!API_URL) {
        setError('Checkout API is not configured.');
        return;
      }

      if (isResolvingSavedPaymentMethod) {
        setError('Saved card selection is still loading. Please wait a moment.');
        return;
      }

      if (!usingSavedCard) {
        if (!stripe || !elements) {
          setError('Payment form is not ready yet. Please wait a moment.');
          return;
        }
        if (!paymentElementReady) {
          setError('Payment element is still loading. Please wait a moment.');
          return;
        }
      }

      if (catalogBlockingError) {
        setError(catalogBlockingError);
        return;
      }

      setProcessing(true);

      try {
        identifyUserFromLeadCapture(
          billingContact.email,
          {
            name: billingContact.name,
            phone: billingContact.phone,
            lead_type: 'checkout_page',
            checkout_type: 'unified_checkout',
            flow_context: flowContext,
          },
          'checkout_page',
        );

        const userId = isAuthenticated ? user.sub : `temp_${Date.now()}`;
        const token = isAuthenticated ? await getApiToken() : null;
        const checkoutTransactionId = generateCheckoutTransactionId(
          null,
          null,
          `checkout_page_${Date.now()}`,
        );
        const checkoutAttemptId = createCheckoutAttemptId('unified_checkout');

        // Same canonical event, same guard: a buyer who reached checkout and then
        // submitted payment is one begin_checkout, not two.
        trackBeginCheckout({
          dedupeKey: [
            'unified_cart_checkout',
            derivedItems
              .map((item) => item.product?.slug || item.raw.productSlug || 'unknown')
              .join('|'),
            derivedItems
              .map((item) => item.plan?.planKey || item.raw.planKey || 'default')
              .join('|'),
            checkoutExperienceEventProps.checkout_experience_variant || 'control',
          ].join(':'),
          transactionId: checkoutTransactionId,
          source: location.pathname,
          userId,
          user_type: isAuthenticated ? 'registered' : 'guest',
          plan_type: derivedItems
            .map((item) => item.product?.slug || item.raw.productSlug)
            .join(','),
          flow_context: flowContext,
          lead_transaction_id: leadTransactionId,
          ...checkoutExperienceEventProps,
          ...utmData,
        });

        if (!cart?.items?.length) {
          setError('Your cart is empty. Please add a plan before checking out.');
          setProcessing(false);
          return;
        }

        const selectedPlanPayload = buildSelectedPlanPayload();
        const productSlugs = cart.items.map((item) => item.productSlug);
        const currentAffiliateCode = getAffiliateCodeForApi();
        console.info('[CheckoutPage] Affiliate checkout debug', {
          currentAffiliateCode: currentAffiliateCode || null,
          utmAffiliateCode:
            utmData?.affiliateCode || utmData?.affiliate_code || utmData?.aff || null,
          attributionAffiliateCode:
            attributionParams?.affiliateCode ||
            attributionParams?.affiliate_code ||
            attributionParams?.aff ||
            null,
          productSlugs,
          hasStoredUtmData: Boolean(utmData),
        });

        const attemptAutoLogin = async (conversionResult, redirectUrl, stripeProof = {}) => {
          try {
            const autoLoginBody = {};
            const paymentIntentId =
              stripeProof.paymentIntentId ||
              conversionResult.paymentIntentId ||
              conversionResult.invoicePaymentIntentId;
            const paymentIntentClientSecret =
              stripeProof.paymentIntentClientSecret ||
              conversionResult.paymentIntentClientSecret ||
              conversionResult.invoiceClientSecret ||
              conversionResult.clientSecret;
            const setupIntentId = stripeProof.setupIntentId || conversionResult.setupIntentId;
            const setupIntentClientSecret =
              stripeProof.setupIntentClientSecret || conversionResult.setupIntentClientSecret;
            if (paymentIntentId) autoLoginBody.payment_intent_id = paymentIntentId;
            if (paymentIntentClientSecret)
              autoLoginBody.payment_intent_client_secret = paymentIntentClientSecret;
            if (setupIntentId) autoLoginBody.setup_intent_id = setupIntentId;
            if (setupIntentClientSecret)
              autoLoginBody.setup_intent_client_secret = setupIntentClientSecret;
            if (conversionResult.subscriptionId) {
              autoLoginBody.subscription_id = conversionResult.subscriptionId;
            }
            if (billingContact.email) {
              autoLoginBody.email = billingContact.email;
            }
            if (conversionResult.customerId) {
              autoLoginBody.customer_id = conversionResult.customerId;
            }

            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.delete('payment_intent');
            currentUrl.searchParams.delete('payment_intent_client_secret');
            currentUrl.searchParams.delete('redirect_status');

            autoLoginBody.redirectTo = redirectUrl || currentUrl.pathname + currentUrl.search;

            const autoLoginResponse = await fetchWithTimeout(
              `${API_URL}/stripe/autoLoginAfterPurchase`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(autoLoginBody),
              },
              AUTO_LOGIN_FETCH_TIMEOUT_MS,
            );

            if (!autoLoginResponse.ok) {
              throw new Error(`Auto-login request failed: ${autoLoginResponse.status}`);
            }

            const autoLoginData = await autoLoginResponse.json();
            if (
              await completeStripeAutoLogin(autoLoginData, {
                handleCustomJwtAuth,
                fallbackUrl: autoLoginBody.redirectTo,
                navigate,
              })
            ) {
              return;
            }

            navigate(autoLoginBody.redirectTo, { replace: true });
          } catch (autoLoginError) {
            console.error('[CheckoutPage] Auto-login error', autoLoginError);
            navigate(redirectUrl || '/account-settings/subscriptions', {
              replace: true,
            });
          }
        };

        const finalizeSuccess = async (conversionResult, stripeProof = {}) => {
          const creditCoveredActivation = Boolean(
            conversionResult?.checkoutComplete &&
            conversionResult?.nextStep === 'subscription_active' &&
            conversionResult?.invoiceAmountDue === 0,
          );
          if (!creditCoveredActivation) {
            pushEvent({
              event: ANALYTICS_EVENTS.PAYMENT_SUCCESS,
              transaction_id:
                conversionResult.subscriptionId ||
                conversionResult.paymentIntentId ||
                checkoutTransactionId,
              currency: totals.currency,
              // Same commitment basis as begin_checkout so the two events are
              // comparable in Google and Meta.
              value: checkoutAnalyticsValue(derivedItems),
              plan_type: derivedItems
                .map((item) => item.product?.slug || item.raw.productSlug)
                .join(','),
              source: location.pathname,
              userId,
              user_type: isAuthenticated ? 'registered' : 'guest',
              lead_transaction_id: leadTransactionId,
              how_found_us: contact.howFoundUs || null,
              how_found_us_details: contact.howFoundUsOtherText?.trim() || null,
              ...utmData,
            });
          }

          setCheckoutSucceeded(true);
          popToast(
            creditCoveredActivation
              ? 'Subscription active! Redirecting...'
              : 'Payment successful! Redirecting...',
            'success',
          );

          try {
            const receiptItems = derivedItems.map(({ raw, product, plan }) => ({
              key: `${raw.productSlug}-${raw.planKey}`,
              name:
                raw.productSlug === 'marketplace-strategy' && raw.metadata?.strategyName
                  ? raw.metadata.strategyName
                  : product?.name || raw.productSlug,
              planLabel: plan?.label || plan?.planKey || raw.planKey,
              amount: Number.isFinite(plan?.amount) ? plan.amount : null,
              currency: plan?.currency || null,
              interval: plan?.interval || null,
            }));
            setCompletedItems(receiptItems);
          } catch (captureError) {
            console.warn('[CheckoutPage] Unable to capture receipt items', captureError);
            setCompletedItems([]);
          }

          try {
            await clearCart();
          } catch (clearError) {
            console.warn('[CheckoutPage] Failed to clear cart after success', clearError);
          }

          try {
            refreshPaymentStatus();
          } catch (refreshError) {
            console.warn('[CheckoutPage] Failed to refresh payment status', refreshError);
          }

          const redirectUrl = buildPostCheckoutSurveyUrl(
            buildRedirectUrl() || '/account-settings/subscriptions',
            conversionResult,
          );
          await attemptAutoLogin(conversionResult, redirectUrl, stripeProof);
        };

        const termsPayload = buildTermsPayload();
        const smsConsentPayload = buildSmsConsentPayload({
          source: 'unified_checkout_page',
          phoneNumber: billingContact.phone,
          accepted: smsConsentAccepted,
        });

        const baseConvertPayload = {
          email: billingContact.email,
          phoneNumber: billingContact.phone,
          ...(smsConsentPayload ? { smsConsent: smsConsentPayload } : {}),
          productSlugs,
          items: cart.items,
          selectedPlanKeys: selectedPlanPayload,
          checkoutAttemptId,
          leadTransactionId,
          terms: termsPayload,
          flowContext: flowContext || null,
          cartMetadata: checkoutMetadata,
          howFoundUs: contact.howFoundUs || null,
          howFoundUsOtherText: contact.howFoundUsOtherText?.trim() || null,
          // Pass coupon code for discount application
          ...(appliedCoupon?.code ? { promoCode: appliedCoupon.code } : {}),
          ...(currentAffiliateCode
            ? {
                affiliateCode: currentAffiliateCode,
                affiliate_code: currentAffiliateCode,
                aff: currentAffiliateCode,
              }
            : {}),
        };

        if (usingSavedCard) {
          if (!selectedSavedPaymentMethodId) {
            setError('Please select a saved card before continuing.');
            setProcessing(false);
            return;
          }

          const conversion = await convertLeadToTrial(
            {
              ...baseConvertPayload,
              paymentMethodId: selectedSavedPaymentMethodId,
            },
            { apiUrl: API_URL, authToken: token },
          );
          await maybeConfirmInvoicePayment(conversion);
          await finalizeSuccess(conversion);
          return;
        }

        if (!stripe || !elements) {
          setError('Payment processor is not ready. Please refresh and try again.');
          setProcessing(false);
          return;
        }

        const payload = {
          ...getWebsiteThemeAttribution(),
          items: cart.items,
          flowContext,
          cartMetadata: checkoutMetadata,
          redirectTarget: redirectTargetFromCart,
          checkoutAttemptId,
          leadTransactionId,
          contact: {
            name: billingContact.name,
            email: billingContact.email,
            phone: billingContact.phone || null,
          },
          email: billingContact.email,
          customerName: billingContact.name,
          phoneNumber: billingContact.phone,
          terms: termsPayload,
          ...(smsConsentPayload ? { smsConsent: smsConsentPayload } : {}),
          paymentType: 'card',
          forceSubscriptionCreation: true,
          subscriptionPaymentIntentMode: true,
          // Pass coupon code to backend for discount application
          ...(appliedCoupon?.code ? { promoCode: appliedCoupon.code } : {}),
          ...(utmData?.utm_source ? { utmSource: utmData.utm_source } : {}),
          ...(utmData?.utm_medium ? { utmMedium: utmData.utm_medium } : {}),
          ...(utmData?.utm_campaign ? { utmCampaign: utmData.utm_campaign } : {}),
          ...(utmData?.utm_term ? { utmTerm: utmData.utm_term } : {}),
          ...(utmData?.utm_content ? { utmContent: utmData.utm_content } : {}),
          landingPage:
            utmData?.landing_page || sessionStorage.getItem('landingPage') || document.referrer,
          pagePath: window.location.pathname,
          pageTitle: document.title,
          howFoundUs: contact.howFoundUs || null,
          howFoundUsOtherText: contact.howFoundUsOtherText?.trim() || null,
          ...attributionParams,
          ...(currentAffiliateCode
            ? {
                affiliateCode: currentAffiliateCode,
                affiliate_code: currentAffiliateCode,
                aff: currentAffiliateCode,
              }
            : {}),
        };

        console.info('[CheckoutPage] Payment intent affiliate payload debug', {
          affiliateCode: payload.affiliateCode || payload.affiliate_code || payload.aff || null,
          productSlugs,
          forceSubscriptionCreation: payload.forceSubscriptionCreation,
          subscriptionPaymentIntentMode: payload.subscriptionPaymentIntentMode,
        });

        const response = await fetchWithTimeout(
          `${API_URL}/stripe/create-payment-intent`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Request-Id': checkoutAttemptId,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(payload),
          },
          PAYMENT_INTENT_FETCH_TIMEOUT_MS,
        );

        const data = await response.json();

        if (!response.ok) {
          setError(data?.message || 'Failed to process your payment.');
          setProcessing(false);
          return;
        }

        const checkoutResponse = classifyCheckoutResponse(data);
        if (checkoutResponse.kind === 'completed_subscription') {
          await finalizeSuccess(data);
          return;
        }

        const paymentIntentResponse = Boolean(data?.clientSecret && data?.paymentIntentId);
        const subscriptionPaymentIntentResponse = Boolean(data?.subscriptionId);

        if (paymentIntentResponse) {
          const { error: submitError } = await elements.submit();
          if (submitError) {
            setError(submitError.message || 'Failed to submit payment details.');
            setProcessing(false);
            return;
          }

          const { error: confirmError, paymentIntent } = await confirmPaymentWithClient(
            stripe,
            elements,
            {
              clientSecret: data.clientSecret,
              redirect: 'if_required',
            },
          );

          if (confirmError) {
            setError(confirmError.message || 'Payment confirmation failed. Please try again.');
            setProcessing(false);
            return;
          }

          const confirmedPaymentIntent = paymentIntent || {
            id: data.paymentIntentId,
            status: data.paymentIntentStatus || data.status || 'processing',
          };

          const validStatuses = new Set(['succeeded', 'processing', 'requires_capture']);
          if (!confirmedPaymentIntent?.id || !validStatuses.has(confirmedPaymentIntent.status)) {
            setError('Payment confirmation failed. Please try again.');
            setProcessing(false);
            return;
          }

          if (!subscriptionPaymentIntentResponse) {
            await finalizeSuccess(data, {
              paymentIntentId: confirmedPaymentIntent.id,
              paymentIntentClientSecret: data.clientSecret,
            });
            return;
          }

          const conversion = await convertLeadToTrial(
            {
              ...baseConvertPayload,
              subscriptionId: data.subscriptionId,
              paymentIntentId: data.paymentIntentId,
              paymentIntentClientSecret: data.clientSecret,
              customerId: data.customerId,
            },
            { apiUrl: API_URL, authToken: token },
          );

          await maybeConfirmInvoicePayment(conversion);
          await finalizeSuccess(conversion, {
            paymentIntentId: confirmedPaymentIntent.id || data.paymentIntentId,
            paymentIntentClientSecret: data.clientSecret,
          });
          return;
        }

        if (!data?.clientSecret || !data?.customerId || !data?.setupIntentId) {
          setError('Payment initialization failed. Please try again.');
          setProcessing(false);
          return;
        }

        const { error: submitError } = await elements.submit();
        if (submitError) {
          setError(submitError.message || 'Failed to submit payment details.');
          setProcessing(false);
          return;
        }

        const { error: confirmError, setupIntent } = await confirmSetupWithClient(
          stripe,
          elements,
          {
            clientSecret: data.clientSecret,
            redirect: 'if_required',
          },
        );

        if (confirmError) {
          setError(confirmError.message || 'Payment confirmation failed. Please try again.');
          setProcessing(false);
          return;
        }

        const confirmedSetupIntent = setupIntent || {
          id: data.setupIntentId,
          customer: data.customerId,
          status: 'succeeded',
        };
        if (!confirmedSetupIntent || confirmedSetupIntent.status !== 'succeeded') {
          setError('Payment method validation failed. Please try again.');
          setProcessing(false);
          return;
        }

        const conversion = await convertLeadToTrial(
          {
            ...baseConvertPayload,
            setupIntentId: confirmedSetupIntent.id || data.setupIntentId,
            setupIntentClientSecret: data.clientSecret,
            customerId:
              (confirmedSetupIntent.customer &&
                confirmedSetupIntent.customer.toString &&
                confirmedSetupIntent.customer.toString()) ||
              data.customerId,
          },
          { apiUrl: API_URL, authToken: token },
        );

        await maybeConfirmInvoicePayment(conversion);
        await finalizeSuccess(conversion, {
          setupIntentId: confirmedSetupIntent.id || data.setupIntentId,
          setupIntentClientSecret: data.clientSecret,
        });
      } catch (submitError) {
        console.error('[CheckoutPage] Unexpected checkout error', submitError);
        setError(submitError?.message || 'An unexpected error occurred. Please try again.');
        setProcessing(false);
      }
    },
    [
      appliedCoupon,
      attributionParams,
      billingContact.email,
      billingContact.name,
      billingContact.phone,
      buildPostCheckoutSurveyUrl,
      buildRedirectUrl,
      maybeConfirmInvoicePayment,
      buildSelectedPlanPayload,
      cart,
      cartHasItems,
      catalogBlockingError,
      checkoutExperienceEventProps,
      checkoutMetadata,
      clearCart,
      contact.howFoundUs,
      contact.howFoundUsOtherText,
      contactFieldsComplete,
      derivedItems,
      elements,
      flowContext,
      getApiToken,
      handleCustomJwtAuth,
      hasProfileIdentity,
      howFoundUsComplete,
      isAuthenticated,
      isResolvingSavedPaymentMethod,
      leadTransactionId,
      location.pathname,
      navigate,
      paymentElementReady,
      popToast,
      processing,
      redirectTargetFromCart,
      refreshPaymentStatus,
      selectedSavedPaymentMethodId,
      setError,
      smsConsentAccepted,
      stripe,
      termsAccepted,
      termsAcceptanceError,
      totals.currency,
      totals.oneTimeCents,
      totals.recurringCents,
      user,
      utmData,
      usingSavedCard,
    ],
  );

  const readinessIssues = useMemo(() => {
    if (checkoutSucceeded) {
      return [];
    }
    const issues = [];
    if (catalogBlockingError) {
      issues.push('catalog');
    }
    if (!contactIdentityFieldsComplete) {
      issues.push('contact');
    }
    if (!howFoundUsComplete) {
      issues.push('how_found_us');
    }
    if (!cartHasItems) {
      issues.push('cart');
    }
    if (!termsAccepted) {
      issues.push('terms');
    }
    if (isResolvingSavedPaymentMethod) {
      issues.push('saved_card_selection');
    } else if (shouldRenderPaymentElement && !usingSavedCard && !paymentElementReady) {
      issues.push('payment_form');
    }
    if (usingSavedCard && !selectedSavedPaymentMethodId) {
      issues.push('saved_card_selection');
    }
    return issues;
  }, [
    catalogBlockingError,
    contactIdentityFieldsComplete,
    cartHasItems,
    howFoundUsComplete,
    isResolvingSavedPaymentMethod,
    checkoutSucceeded,
    paymentElementReady,
    selectedSavedPaymentMethodId,
    shouldRenderPaymentElement,
    termsAccepted,
    usingSavedCard,
  ]);

  if (!cart || cartLoading) {
    return (
      <div className="checkout-loading">
        <Spinner animation="border" role="status" aria-label="Preparing checkout" />
      </div>
    );
  }

  if (alreadyOwnedItems.length > 0) {
    const names = alreadyOwnedItems.map(({ product }) => getCheckoutProductDisplayName(product));
    return (
      <Alert variant="info" className="checkout-catalog-alert">
        <strong>You already own {names.join(', ')}.</strong> Open it from your account; there is
        nothing else to purchase here.
      </Alert>
    );
  }

  return (
    <>
      <form
        className={`checkout-form-wrapper${''}`}
        data-bs-theme={checkoutTheme}
        onSubmit={submitPayment}
      >
        {contextBannerMessage ? (
          <div className="checkout-context-banner">{contextBannerMessage}</div>
        ) : null}

        {catalogBlockingError ? (
          <Alert variant="danger" className="checkout-catalog-alert">
            <div>{catalogBlockingError}</div>
            <div className="checkout-catalog-alert__actions">
              <button
                type="button"
                className="btn btn-light btn-sm"
                onClick={refreshCatalog}
                disabled={catalogLoading}
              >
                {catalogLoading ? 'Retrying…' : 'Retry'}
              </button>
            </div>
          </Alert>
        ) : null}

        <CartSummary
          items={derivedItems}
          totals={totals}
          allowRemoval={allowRemoval}
          onPlanChange={handlePlanChange}
          onQuantityChange={handleQuantityChange}
          onRemove={requestRemoveItem}
          metadata={metadata}
          title={profile?.orderSummaryTitle || 'Order summary'}
          checkoutSucceeded={checkoutSucceeded}
          completedItems={completedItems}
        />

        {/* Coupon code input section - only show when checkout hasn't succeeded */}
        {!checkoutSucceeded && (
          <CouponCodeSection
            appliedCoupon={appliedCoupon}
            onApplyCoupon={handleApplyCoupon}
            onRemoveCoupon={handleRemoveCoupon}
            isValidating={isValidatingCoupon}
            validationError={couponValidationError}
            disabled={processing || cartPending}
            inputValue={couponCodeInput}
            setInputValue={setCouponCodeInput}
            isExpanded={couponCodeExpanded}
            setIsExpanded={setCouponCodeExpanded}
          />
        )}

        {/* Wait for profile to load for authenticated users before showing contact section */}
        {isAuthenticated && profileLoading ? (
          <div className="checkout-contact-loading">
            <span>Loading your info...</span>
          </div>
        ) : hasProfileIdentity ? (
          <>
            <div className="checkout-contact-summary">
              <div>
                <strong>Name:</strong> {billingContact.name || 'Not provided'}
              </div>
              <div>
                <strong>Email:</strong> {billingContact.email || 'Not provided'}
              </div>
              {billingContact.phone ? (
                <div>
                  <strong>Phone:</strong> {billingContact.phone}
                </div>
              ) : null}
            </div>
            {billingContact.phone ? (
              <SmsConsentCheckbox
                id="checkout-contact-sms-consent"
                checked={smsConsentAccepted}
                onChange={setSmsConsentAccepted}
                disabled={processing || cartPending}
                compact
              />
            ) : null}
          </>
        ) : (
          <CheckoutContactFields
            value={contact}
            onChange={handleContactChange}
            disabled={processing || cartPending}
            smsConsentId="checkout-contact-sms-consent"
            smsConsentAccepted={smsConsentAccepted}
            onSmsConsentChange={setSmsConsentAccepted}
            collapseWhenComplete={contactWasPrefilledOnLoad}
          />
        )}

        {savedMethods.hasMethods ? (
          <SavedPaymentMethods
            methods={savedMethods.paymentMethods}
            selectedId={selectedSavedPaymentMethodId}
            loading={savedMethods.loading}
            pending={savedMethods.pending}
            activeMode={paymentMode}
            onSwitchMode={handleSwitchPaymentMode}
            onSelect={handleSelectSavedPaymentMethod}
            onRemove={(id) => setPendingCardRemovalId(id)}
            onSetDefault={async (id) => {
              try {
                await savedMethods.setDefault(id);
                setSelectedSavedPaymentMethod(id);
                setPaymentMode(PAYMENT_MODES.SAVED);
              } catch (defaultError) {
                console.error('[CheckoutPage] Failed to set default payment method', defaultError);
                setError(defaultError?.message || 'Failed to update default card.');
              }
            }}
            resolving={isResolvingSavedPaymentMethod}
          />
        ) : null}

        {usingSavedCard ? (
          <div className="checkout-selected-card-banner">
            <FaLock /> Using saved card ending in{' '}
            {
              savedMethods.paymentMethods.find(
                (method) => method.id === selectedSavedPaymentMethodId,
              )?.last4
            }
          </div>
        ) : null}

        {usingNewCard && !isResolvingSavedPaymentMethod && shouldRenderPaymentElement ? (
          <CheckoutPaymentContainer>
            <StripeErrorBoundary>
              <PaymentElement
                key={paymentElementMountKey}
                options={paymentElementOptions}
                onReady={() => setPaymentElementReady(true)}
                onChange={() => setPaymentElementReady(true)}
                onLoadError={(paymentError) => {
                  console.error('[CheckoutPage] PaymentElement load error', paymentError);
                  setError('Payment form failed to load. Please refresh and try again.');
                }}
              />
            </StripeErrorBoundary>
          </CheckoutPaymentContainer>
        ) : null}

        {paymentElementTimedOut && !checkoutSucceeded ? (
          <Alert variant="warning" className="checkout-catalog-alert">
            <div>The embedded payment form is taking too long to load.</div>
            <div className="checkout-catalog-alert__actions">
              <button
                type="button"
                className="btn btn-light btn-sm"
                onClick={openHostedCheckout}
                disabled={processing || cartPending}
              >
                Open secure Stripe checkout
              </button>
            </div>
          </Alert>
        ) : null}

        {paymentHighlights.length ? (
          <div className="checkout-payment-highlights">
            {paymentHighlights.map((highlight) => (
              <span key={highlight}>{highlight}</span>
            ))}
          </div>
        ) : null}

        {error && !checkoutSucceeded ? <div className="checkout-error-banner">{error}</div> : null}

        <div className="checkout-lower-details">
          <HowFoundUsDropdown
            value={contact.howFoundUs}
            otherText={contact.howFoundUsOtherText}
            onChange={(val) => handleContactChange('howFoundUs', val)}
            onOtherTextChange={(val) => handleContactChange('howFoundUsOtherText', val)}
            disabled={processing}
          />

          <div className="checkout-consent-stack">
            <label className={`terms-checkbox-group ${termsError ? 'error' : ''}`}>
              <input
                type="checkbox"
                id="checkout-terms"
                className="terms-checkbox-input"
                checked={termsAccepted}
                onChange={(event) => {
                  setTermsAccepted(event.target.checked);
                  // Clear error when user accepts terms
                  if (event.target.checked) {
                    setTermsError('');
                    setError('');
                    const termsGroup = document.querySelector('.terms-checkbox-group');
                    if (termsGroup) {
                      termsGroup.classList.remove('error');
                    }
                  }
                }}
                disabled={processing}
                required
              />
              <span className="terms-text">
                I agree to the{' '}
                <a href="/terms" className="terms-link" onClick={(e) => e.stopPropagation()}>
                  Terms of Service
                </a>{' '}
                {termsAdditionalText || 'and authorize this purchase.'}
              </span>
            </label>
            {termsError && <div className="terms-error-message">❌ {termsError}</div>}
          </div>
        </div>

        <CheckoutReadinessChecklist missingKeys={checkoutSucceeded ? [] : readinessIssues} />

        <div className="checkout-cta-wrapper">
          <OverlayTrigger
            placement="top"
            delay={{ show: 150, hide: 0 }}
            overlay={
              !checkoutSucceeded && readinessIssues.length && (!processing || cartPending) ? (
                <Tooltip id="checkout-cta-tooltip">
                  Complete these steps: {readinessIssues.map(buildRequirementLabel).join('; ')}
                </Tooltip>
              ) : (
                <></>
              )
            }
          >
            <span className="checkout-cta-overlay-wrapper">
              <CheckoutCTAButton
                label={ctaLabel}
                processingLabel={ctaProcessingLabel}
                processing={processing}
                disabled={
                  !contactComplete ||
                  processing ||
                  cartPending ||
                  isResolvingSavedPaymentMethod ||
                  (usingSavedCard ? false : !paymentElementReady)
                }
                sparkle={ctaSparkle}
                aria-describedby={
                  !checkoutSucceeded && readinessIssues.length
                    ? 'checkout-readiness-hint'
                    : undefined
                }
              />
            </span>
          </OverlayTrigger>
          {!checkoutSucceeded && readinessIssues.length ? (
            <div id="checkout-readiness-hint" className="checkout-cta-helper">
              Still missing: {readinessIssues.map(buildRequirementLabel).join(', ')}
            </div>
          ) : null}
        </div>
        {reassuranceItems.length ? (
          <div className="checkout-reassurance-list">
            {reassuranceItems.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        ) : null}
      </form>

      <ConfirmationModal
        isOpen={Boolean(pendingRemovalItem)}
        onClose={closeRemoveItemModal}
        onConfirm={confirmRemoveItem}
        title="Remove item?"
        message={removalModalMessage}
        confirmText="Remove"
        cancelText="Keep"
        isProcessing={removingItem}
      />

      <ConfirmationModal
        isOpen={Boolean(pendingCardRemovalId)}
        onClose={() => setPendingCardRemovalId(null)}
        onConfirm={async () => {
          setRemovingCard(true);
          try {
            await savedMethods.remove(pendingCardRemovalId);
            if (selectedSavedPaymentMethodId === pendingCardRemovalId) {
              setSelectedSavedPaymentMethod(INITIAL_SAVED_PAYMENT_METHOD);
              setPaymentMode(PAYMENT_MODES.NEW);
            }
          } catch (removeError) {
            console.error('[CheckoutPage] Failed to remove payment method', removeError);
            setError(removeError?.message || 'Failed to remove saved card.');
          } finally {
            setRemovingCard(false);
            setPendingCardRemovalId(null);
          }
        }}
        title="Remove saved card?"
        message="This card will be removed from your saved payment methods. You can always add a new one later."
        confirmText="Remove"
        cancelText="Keep"
        isProcessing={removingCard}
      />

      {/* Payment toast notifications */}
      <ToastContainer position="top-center" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          bg={checkoutToast.variant}
          show={checkoutToast.show}
          autohide
          delay={checkoutToast.variant === 'success' ? 3000 : 6000}
          onClose={() => setCheckoutToast((s) => ({ ...s, show: false }))}
        >
          <Toast.Body className="text-white">{checkoutToast.msg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

const CheckoutPage = () => {
  const location = useLocation();

  const checkoutUser = useUser();
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [couponValidationError, setCouponValidationError] = useState('');
  const [catalogWithCoupon, setCatalogWithCoupon] = useState(null);
  const [dismissedAutoApplyCoupon, setDismissedAutoApplyCoupon] = useState(false);
  const [couponCodeInput, setCouponCodeInput] = useState('');
  const [couponCodeExpanded, setCouponCodeExpanded] = useState(false);
  const couponState = useMemo(
    () => ({
      appliedCoupon,
      setAppliedCoupon,
      isValidatingCoupon,
      setIsValidatingCoupon,
      couponValidationError,
      setCouponValidationError,
      catalogWithCoupon,
      setCatalogWithCoupon,
      dismissedAutoApplyCoupon,
      setDismissedAutoApplyCoupon,
      couponCodeInput,
      setCouponCodeInput,
      couponCodeExpanded,
      setCouponCodeExpanded,
    }),
    [
      appliedCoupon,
      isValidatingCoupon,
      couponValidationError,
      catalogWithCoupon,
      dismissedAutoApplyCoupon,
      couponCodeInput,
      couponCodeExpanded,
    ],
  );
  const [themeMode] = useThemeMode();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const checkoutThemeParam = queryParams.get('checkoutTheme');
  const isNonProduction = process.env.NODE_ENV !== 'production';

  const hasCheckoutThemeOverride =
    isNonProduction && CHECKOUT_THEME_VARIANTS.includes(checkoutThemeParam);

  const checkoutTheme = hasCheckoutThemeOverride ? checkoutThemeParam : applyThemeMode(themeMode);

  const checkoutExperienceExperiment = useCheckoutExperienceExperiment('unified_cart_checkout');

  const {
    cart,
    loading: cartLoading,
    pending: cartPending,
    updateItem,
    removeItem,
    clearCart,
    debugInfo: cartDebugInfo,
    debugEnabled: checkoutDebugEnabled,
  } = useCheckoutCart();
  const {
    catalog,
    loading: catalogLoading,
    error: catalogError,
    refresh: refreshCatalog,
  } = useProductCatalog();

  const checkoutFallbackCart = useMemo(() => getCheckoutFallbackCart(location), [location]);
  const cartForCheckout = useMemo(() => {
    const serverCartHasItems = Array.isArray(cart?.items) && cart.items.length > 0;
    if (
      checkoutFallbackCart?.items?.length &&
      !(checkoutFallbackCart.cartSynced && serverCartHasItems)
    ) {
      return checkoutFallbackCart;
    }
    return cart;
  }, [cart, checkoutFallbackCart]);
  const catalogForCheckout = useMemo(
    () => mergeCatalogWithFallbackProducts(catalog, checkoutFallbackCart),
    [catalog, checkoutFallbackCart],
  );
  const hasFallbackCatalogProducts =
    catalogForCheckout !== catalog && Array.isArray(catalogForCheckout?.products);
  const cartLoadingForCheckout = cartLoading && !checkoutFallbackCart?.items?.length;
  const catalogLoadingForCheckout =
    catalogLoading && !hasFallbackCatalogProducts && !catalogForCheckout?.products?.length;
  const catalogErrorForCheckout = hasFallbackCatalogProducts ? null : catalogError;
  const isLoading = cartLoadingForCheckout || catalogLoadingForCheckout;
  const metadata = cartForCheckout?.metadata || null;
  const flowContext = cartForCheckout?.flowContext || null;
  const checkoutProfile = CHECKOUT_PROFILE;
  const currentCartItems = Array.isArray(cartForCheckout?.items) ? cartForCheckout.items.length : 0;
  const derivedItemsForPayment = useMemo(
    () => deriveCartItems(cartForCheckout, catalogForCheckout, metadata),
    [cartForCheckout, catalogForCheckout, metadata],
  );

  const savedMethods = useSavedPaymentMethods({
    enabled: !false,
  });
  // Node creates every PaymentIntent for this checkout with
  // setup_future_usage: 'off_session' (stripePaymentIntent.controller.ts) so a
  // pay-in-full buyer still ends up with a card on file. Stripe refuses to
  // confirm when Elements and the PaymentIntent disagree, so Elements must
  // declare it on every purchase, not only recurring ones. On 2026-09-10 the
  // two halves disagreed and no one-time purchase could complete.
  const requiresFutureUsage = true;
  const requiresCardOnlyPaymentMethods = useMemo(
    () =>
      Array.isArray(derivedItemsForPayment.items)
        ? derivedItemsForPayment.items.some(({ plan }) => Boolean(plan?.interval))
        : false,
    [derivedItemsForPayment.items],
  );
  const totalsForPaymentElement = useMemo(
    () => computeTotals(derivedItemsForPayment.items),
    [derivedItemsForPayment.items],
  );
  const paymentElementAmountCents = useMemo(() => {
    const total =
      (totalsForPaymentElement.recurringCents || 0) + (totalsForPaymentElement.oneTimeCents || 0);
    const fallback =
      totalsForPaymentElement.recurringCents || totalsForPaymentElement.oneTimeCents || 0;
    const resolved = total > 0 ? total : fallback;
    return Math.max(50, resolved || 0);
  }, [totalsForPaymentElement.oneTimeCents, totalsForPaymentElement.recurringCents]);
  const elementsContextOptions = useMemo(() => {
    const options = {
      mode: 'payment',
      currency: (totalsForPaymentElement.currency || 'usd').toLowerCase(),
      appearance: { theme: checkoutTheme === 'light' ? 'stripe' : 'night' },
      loader: 'auto',
      amount: paymentElementAmountCents,
    };
    if (requiresFutureUsage) {
      options.setupFutureUsage = 'off_session';
    }
    if (requiresCardOnlyPaymentMethods) {
      options.paymentMethodTypes = ['card'];
    }
    return options;
  }, [
    paymentElementAmountCents,
    requiresCardOnlyPaymentMethods,
    requiresFutureUsage,
    totalsForPaymentElement.currency,
    checkoutTheme,
  ]);
  const elementsContextKey = useMemo(
    () =>
      [
        elementsContextOptions.mode,
        elementsContextOptions.currency,
        elementsContextOptions.paymentMethodTypes?.join(',') || 'automatic',
        paymentElementAmountCents,
        requiresFutureUsage ? 'future' : 'now',
        elementsContextOptions.paymentMethodTypes?.join(',') || 'automatic',
      ].join(':'),
    [
      elementsContextOptions.currency,
      elementsContextOptions.mode,
      elementsContextOptions.paymentMethodTypes,
      paymentElementAmountCents,
      requiresFutureUsage,
    ],
  );
  const hostedCheckoutItems = useMemo(
    () =>
      Array.isArray(derivedItemsForPayment.items)
        ? derivedItemsForPayment.items
            .filter(({ raw, product, plan }) => raw?.productSlug && product && plan)
            .map(({ raw }) => ({
              productSlug: raw.productSlug,
              planKey: raw.planKey,
              quantity: raw.quantity ?? 1,
            }))
        : [],
    [derivedItemsForPayment.items],
  );
  // Land hosted buyers where the custom checkout lands them. Starter and Light
  // buyers get their post-purchase path from the subscriptions page; everyone
  // else (Bootcamp, Pro, All Access) gets the purchase-confirmation survey. A
  // hosted Bootcamp buyer used to land on "No active subscriptions" after
  // paying in full (Dev, 2026-09-11).
  const hostedReturnContext = useMemo(
    () =>
      hostedCheckoutItems.some(({ productSlug }) => isStarterOrLightProduct(productSlug))
        ? 'subscriptions'
        : 'signup_survey',
    [hostedCheckoutItems],
  );
  const debugSummary = useMemo(() => {
    if (!checkoutDebugEnabled || !cartDebugInfo) {
      return null;
    }
    return {
      action: cartDebugInfo.type || 'unknown',
      status: cartDebugInfo.status || 'pending',
      sequence: cartDebugInfo.debugId ?? null,
      timestamp: cartDebugInfo.timestamp ?? null,
      beforeCount: cartDebugInfo.cartBefore?.itemCount ?? null,
      afterCount: cartDebugInfo.cartAfter?.itemCount ?? null,
      message: cartDebugInfo.message ?? null,
      statusCode: cartDebugInfo.statusCode ?? null,
      payloadSummary: cartDebugInfo.payloadSummary || null,
      productSlug: cartDebugInfo.productSlug || null,
      flowContext: cartDebugInfo.flowContext || flowContext || null,
    };
  }, [cartDebugInfo, checkoutDebugEnabled, flowContext]);
  const debugTimestampLabel = useMemo(() => {
    if (!debugSummary?.timestamp) return null;
    try {
      return new Date(debugSummary.timestamp).toLocaleTimeString();
    } catch (error) {
      return String(debugSummary.timestamp);
    }
  }, [debugSummary]);
  const debugPayloadEntries = useMemo(
    () => (debugSummary?.payloadSummary ? Object.entries(debugSummary.payloadSummary) : []),
    [debugSummary],
  );

  if (checkoutExperienceExperiment.isLoading) {
    return (
      <div className="checkout-page bsui-scope" data-bs-theme={checkoutTheme}>
        <div className="checkout-loading">
          <Spinner animation="border" />
          <span>Loading checkout...</span>
        </div>
      </div>
    );
  }

  if (
    checkoutExperienceExperiment.variant === 'stripe_hosted' &&
    checkoutExperienceExperiment.enrolled &&
    !isLoading &&
    hostedCheckoutItems.length > 0 &&
    derivedItemsForPayment.missing.length === 0
  ) {
    return (
      <HostedCheckoutLauncher
        items={hostedCheckoutItems}
        promoCode={appliedCoupon?.code || null}
        returnContext={hostedReturnContext}
        sourceRoute={location.pathname}
        flowContext={flowContext}
        surface="unified_cart_checkout"
        assignment={checkoutExperienceExperiment}
        getApiToken={checkoutUser.getApiToken}
      />
    );
  }

  if (!stripePublishableKey) {
    return (
      <div className="checkout-page checkout-page-error bsui-scope" data-bs-theme={checkoutTheme}>
        <h2>Payment configuration error</h2>
        <p>Stripe is not configured. Please contact support.</p>
      </div>
    );
  }

  const checkoutExperience = (
    <CheckoutStripeElements
      key={elementsContextKey}
      publishableKey={stripePublishableKey}
      options={elementsContextOptions}
    >
      <CheckoutExperience
        cart={cartForCheckout}
        catalog={catalogForCheckout}
        catalogLoading={catalogLoadingForCheckout}
        catalogError={catalogErrorForCheckout}
        refreshCatalog={refreshCatalog}
        cartLoading={cartLoadingForCheckout}
        cartPending={cartPending}
        flowContext={cartForCheckout?.flowContext || null}
        metadata={metadata}
        profile={checkoutProfile}
        updatePlan={(productSlug, planKey) => updateItem(productSlug, { planKey })}
        updateQuantity={(productSlug, payload) => updateItem(productSlug, payload)}
        removeItem={removeItem}
        clearCart={clearCart}
        savedMethods={savedMethods}

        checkoutTheme={checkoutTheme}
        checkoutExperienceExperiment={checkoutExperienceExperiment}
        couponState={couponState}
      />
    </CheckoutStripeElements>
  );

  return (
    <div className="checkout-page bsui-scope" data-bs-theme={checkoutTheme}>
      {checkoutDebugEnabled && debugSummary ? (
        <div className="checkout-debug-banner">
          <div className="checkout-debug-banner__row">
            <span className="checkout-debug-banner__label">Checkout Debug</span>
            <span>Action: {debugSummary.action}</span>
            {debugSummary.sequence != null ? <span>Seq: {debugSummary.sequence}</span> : null}
            <span
              className={`checkout-debug-banner__status checkout-debug-banner__status--${debugSummary.status}`}
            >
              {debugSummary.status}
            </span>
            <span>Cart items: {currentCartItems}</span>
            <span>
              Before → After: {debugSummary.beforeCount ?? '-'} → {debugSummary.afterCount ?? '-'}
            </span>
            {debugTimestampLabel ? <span>Updated at: {debugTimestampLabel}</span> : null}
            {debugSummary.flowContext ? <span>Flow: {debugSummary.flowContext}</span> : null}
            {debugSummary.productSlug ? <span>Product: {debugSummary.productSlug}</span> : null}
          </div>
          {debugPayloadEntries.length ? (
            <div className="checkout-debug-banner__row checkout-debug-banner__row--muted">
              {debugPayloadEntries.map(([key, value]) => (
                <span key={key}>
                  {key}: {String(value)}
                </span>
              ))}
            </div>
          ) : null}
          {debugSummary.message ? (
            <div className="checkout-debug-banner__message">
              {debugSummary.message}
              {debugSummary.statusCode ? ` (status ${debugSummary.statusCode})` : ''}
            </div>
          ) : null}
        </div>
      ) : null}
      <header className="checkout-page__hero">
        <h1>{checkoutProfile.heroTitle}</h1>
        {checkoutProfile.heroSubtitle ? (
          <p className="checkout-page__subtitle">{checkoutProfile.heroSubtitle}</p>
        ) : null}
      </header>

      {checkoutProfile.showTrustStrip ? (
        <div className="checkout-trust-strip">
          <TrustStrip />
        </div>
      ) : null}

      <Container>
        <Row className="justify-content-center">
          <Col lg={8} md={10}>
            {isLoading ? (
              <div className="checkout-loading">
                <Spinner animation="border" role="status" aria-label="Loading cart" />
              </div>
            ) : (
              checkoutExperience
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CheckoutPage;

const buildRequirementLabel = (key) => {
  switch (key) {
    case 'contact':
      return 'Complete billing contact details';
    case 'how_found_us':
      return 'Tell us how you found BotSpot';
    case 'terms':
      return 'Accept the Terms of Service';
    case 'cart':
      return 'Add at least one plan to your cart';
    case 'payment_form':
      return 'Wait for the payment form to finish loading';
    case 'saved_card_selection':
      return 'Select a saved card or choose new card';
    case 'catalog':
      return 'Resolve catalog issues before checkout';
    default:
      return 'Complete all required information';
  }
};

const CheckoutReadinessChecklist = ({ missingKeys }) => {
  if (!missingKeys.length) {
    return null;
  }

  return (
    <div className="checkout-readiness-card" role="status" aria-live="polite">
      <div className="checkout-readiness-card__header">Before you can check out</div>
      <ul className="checkout-readiness-card__list">
        {missingKeys.map((key) => (
          <li key={key}>
            <span className="checkout-readiness-card__bullet">•</span>
            {buildRequirementLabel(key)}
          </li>
        ))}
      </ul>
    </div>
  );
};
